import { useState, useRef, useEffect, useMemo, useCallback } from "react";

// Ref for scrolling to top
const topOfPageRef = { current: null };
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, apiClient, getErrorMessage } from "@/lib/api";
import { useGuidance } from "@/lib/GuidanceContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import DashboardLayout from "@/components/layout/DashboardLayout";
import ProgressMessages from "@/components/ProgressMessages";
import VoiceInput from "@/components/VoiceInput";
import ListingDetailsForm from "@/components/ListingDetailsForm";
import FieldPositionMapper from "@/components/FieldPositionMapper";
import VideoCreditsModal from "@/components/VideoCreditsModal";
import AutoPilotSetup from "@/components/AutoPilotSetup";
import { 
  ArrowRight, 
  ArrowLeft,
  Loader2,
  Sparkles,
  Check,
  Calendar,
  Globe,
  Layers,
  Clock,
  Plus,
  Volume2,
  AlertTriangle,
  Upload,
  X,
  Mic,
  Mail,
  Target,
  Video,
  Image as ImageIcon,
  FileText,
  Crown,
  Zap,
  Scissors,
  User,
  UserCircle,
  Layout,
  ChevronRight,
  ChevronDown,
  AlertCircle,
  ShoppingCart,
  Camera,
  History
} from "lucide-react";

// Import modular components and constants - DIRECT IMPORTS to avoid TDZ webpack bug
import { platforms, languageOptions, inputOptions, urlIntentOptions, animationOptions, listingCategories, textPositions } from "@/components/create-content/constants";
import { StepIndicator } from "@/components/create-content/StepIndicator";
import { GeneratingStep } from "@/components/create-content/GeneratingStep";
import { CompletionStep } from "@/components/create-content/CompletionStep";
import { PlatformSelector } from "@/components/create-content/PlatformSelector";
import { LanguageSelector } from "@/components/create-content/LanguageSelector";
import { BrandingSelector } from "@/components/create-content/BrandingSelector";
import { InputTypeSelector } from "@/components/create-content/InputTypeSelector";
import { OutputTypeSelector } from "@/components/create-content/OutputTypeSelector";
import { UrlInput } from "@/components/create-content/UrlInput";
import { TextInput } from "@/components/create-content/TextInput";
import { FileUpload } from "@/components/create-content/FileUpload";
import { ScheduleTypeSelector } from "@/components/create-content/ScheduleTypeSelector";
import { ContentStyleSelector } from "@/components/create-content/ContentStyleSelector";
import { InputContextField } from "@/components/create-content/InputContextField";
import { UrlIntentWithContext } from "@/components/create-content/UrlIntentWithContext";
import { IntentAndStyleSelector } from "@/components/create-content/IntentAndStyleSelector";
import TemplateSelector from "@/components/TemplateSelector";
import MultiImageCollage from "@/components/MultiImageCollage";
import TikTokSettingsPanel from "@/components/TikTokSettingsPanel";
import TikTokCondensedSettings from "@/components/TikTokCondensedSettings";
import PlatformPermissionsChecklist from "@/components/PlatformPermissionsChecklist";

export default function CreateContentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUser } = useAuth();
  const { markContentCreated } = useGuidance();
  const fileInputRef = useRef(null);
  
  // State persistence key and expiry (2 hours - reduced from 24h per user feedback)
  const STORAGE_KEY = 'viraly_create_draft';
  const STORAGE_EXPIRY_MS = 2 * 60 * 60 * 1000; // 2 hours
  
  // Check if there's a saved draft on mount
  const getSavedDraft = useCallback(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { data, timestamp } = JSON.parse(saved);
        // Check if expired
        if (Date.now() - timestamp > STORAGE_EXPIRY_MS) {
          localStorage.removeItem(STORAGE_KEY);
          return null;
        }
        return data;
      }
    } catch (e) {
      localStorage.removeItem(STORAGE_KEY);
    }
    return null;
  }, []);
  
  // Save draft to localStorage
  const saveDraft = useCallback((draftData) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        data: draftData,
        timestamp: Date.now()
      }));
    } catch (e) {
      console.error('Failed to save draft:', e);
    }
  }, []);
  
  // Clear draft
  const clearDraft = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
  }, []);
  
  // Prefill from other pages
  const prefillText = location.state?.prefillText || "";
  const fromViralSimulator = !!prefillText;
  const fromPostAnalyzer = location.state?.fromPostAnalyzer || false;
  const showInputSelection = location.state?.showInputSelection || false;
  const analyzedContent = location.state?.analyzedContent || null;
  
  // Handle URL parameters for mode selection
  const searchParams = new URLSearchParams(location.search);
  const modeParam = searchParams.get('mode');
  
  // NEW INPUT-FIRST FLOW:
  // Step 1: Select Input Type + Enter Input
  // Step 2: Select Output Type (Post/Video)
  // Step 3: Platforms + Branding + Language
  // Step 4: Generating
  // Step 5: Done
  const [step, setStep] = useState(1);
  
  // Auto-Pilot vs Create Your Way mode
  // Reset to selection state when coming from Post Analyzer or when no mode specified
  const [showAutoPilotSetup, setShowAutoPilotSetup] = useState(modeParam === 'autopilot');
  const [showCreateYourWay, setShowCreateYourWay] = useState(
    modeParam === 'manual' && !fromPostAnalyzer && !showInputSelection
  );
  const [autoPilotStatus, setAutoPilotStatus] = useState(null);
  const [autoPilotStatusLoading, setAutoPilotStatusLoading] = useState(true);
  
  // Step 1: Input
  const [inputType, setInputType] = useState(null); // "url", "text", "image", "video"
  const [inputUrl, setInputUrl] = useState("");
  const [inputText, setInputText] = useState(prefillText);
  const [inputFile, setInputFile] = useState(null);
  const [inputFiles, setInputFiles] = useState([]); // Multiple images support
  const [filePreview, setFilePreview] = useState(null);
  
  // Multi-Image Collage Settings
  const [collageHeroIndex, setCollageHeroIndex] = useState(0);
  const [collageLayout, setCollageLayout] = useState("open_house");
  const [collageTransition, setCollageTransition] = useState("fade");
  const [collageExpanded, setCollageExpanded] = useState(true);
  
  // Step 2: Output
  const [outputType, setOutputType] = useState(null); // "post" or "video"
  const [videoDuration, setVideoDuration] = useState(10); // Default 10 seconds for better content
  const [videoQuality, setVideoQuality] = useState("720p"); // "720p" or "1080p"
  const [scheduleType, setScheduleType] = useState("single"); // "single" or "bulk"
  const [days, setDays] = useState(7);
  const [postsPerDay, setPostsPerDay] = useState(1);
  const [scheduleTimePreference, setScheduleTimePreference] = useState("ai_suggested"); // "ai_suggested" or "custom"
  const [customScheduleTime, setCustomScheduleTime] = useState(""); // HH:MM format for custom time
  // For Image→Video bulk: choice between same video or AI-inspired variations
  const [bulkVideoMode, setBulkVideoMode] = useState("same"); // "same" = same video different posts, "inspired" = AI generates unique videos
  // For Image/URL/Text→Post bulk: content and template options
  const [bulkContentMode, setBulkContentMode] = useState("same_content_same_template"); // Options: same_content_same_template, same_content_different_template, different_content_different_template
  const [bulkImageMode, setBulkImageMode] = useState("same_image"); // For image input: "same_image" or "inspired_images"
  // For bulk with different content/templates - per-day details
  const [bulkDayDetails, setBulkDayDetails] = useState({}); // { 1: { template: null, details: {} }, 2: {...}, ... }
  const [expandedBulkDay, setExpandedBulkDay] = useState(null); // Which day card is expanded
  
  // Video Credits System
  const [videoCreditsInfo, setVideoCreditsInfo] = useState(null);
  const [showVideoCreditsModal, setShowVideoCreditsModal] = useState(false);
  const [insufficientCreditsOptions, setInsufficientCreditsOptions] = useState(null);
  const [showPostCreditsModal, setShowPostCreditsModal] = useState(false);  // Modal for insufficient post credits
  const [postCreditsNeeded, setPostCreditsNeeded] = useState(0);  // How many more credits needed
  const [showVoiceoverTrimModal, setShowVoiceoverTrimModal] = useState(false);
  const [originalVoiceoverDuration, setOriginalVoiceoverDuration] = useState(0);
  const [userVoiceoverFile, setUserVoiceoverFile] = useState(null);
  const [selectedVoice, setSelectedVoice] = useState("nova"); // Voice for AI narration
  
  // Available TTS voices
  const voiceOptions = [
    { id: "nova", name: "Nova", description: "Energetic, upbeat", style: "social media", recommended: true },
    { id: "shimmer", name: "Shimmer", description: "Bright, cheerful", style: "fun" },
    { id: "coral", name: "Coral", description: "Warm, friendly", style: "approachable" },
    { id: "alloy", name: "Alloy", description: "Neutral, balanced", style: "professional" },
    { id: "echo", name: "Echo", description: "Smooth, calm", style: "relaxed" },
    { id: "onyx", name: "Onyx", description: "Deep, authoritative", style: "serious" },
    { id: "fable", name: "Fable", description: "Expressive, storytelling", style: "engaging" },
    { id: "sage", name: "Sage", description: "Wise, measured", style: "educational" },
  ];
  
  // URL Intent (Phase 3)
  const [urlIntent, setUrlIntent] = useState("promotional"); // What user wants to create from URL
  
  // Content Style (MANDATORY) - Affects voiceover, music, image style, captions
  const [contentStyle, setContentStyle] = useState("professional");
  const [contentStyleOptions, setContentStyleOptions] = useState([]);
  
  // Image Source Selection for Picture Posts
  const [imageSource, setImageSource] = useState(null); // "stock" or "ai" or null (auto-detect)
  const [imageSourceRecommendation, setImageSourceRecommendation] = useState(null);
  const [stockPhotoResults, setStockPhotoResults] = useState([]);
  const [selectedStockPhoto, setSelectedStockPhoto] = useState(null);
  const [stockPhotoSearching, setStockPhotoSearching] = useState(false);
  
  // Smart Image Mode - For AI Generated images only
  // "auto" = AI detects if content is personal service (tutoring, coaching) and avoids faces
  // "abstract" = Force abstract/professional imagery without faces
  // "lifestyle" = Allow natural lifestyle imagery with people
  const [smartImageMode, setSmartImageMode] = useState("auto");
  
  // Brand Profile Completeness & Location Wizard
  const [showLocationWizard, setShowLocationWizard] = useState(false);
  const [locationWizardData, setLocationWizardData] = useState({
    city: "",
    country: "",
    region: ""
  });
  const [profileCompleteness, setProfileCompleteness] = useState(null);
  
  // Draft Resume Prompt - show user choice to resume or start fresh
  const [showDraftPrompt, setShowDraftPrompt] = useState(false);
  const [savedDraftData, setSavedDraftData] = useState(null);
  
  // Context field for URL/Image inputs - helps us understand user's intent better
  const [inputContext, setInputContext] = useState(""); // "Create promotional posts like this website"
  
  // Connected social accounts - fetch from API
  const [socialAccounts, setSocialAccounts] = useState([]);
  
  useEffect(() => {
    const fetchSocialAccounts = async () => {
      try {
        const res = await apiClient.get("/social/");
        setSocialAccounts(res.data || []);
      } catch (err) {
        console.log("Failed to fetch social accounts");
      }
    };
    if (user) fetchSocialAccounts();
  }, [user]);
  
  // Fetch Auto-Pilot status to show activated badge on Create page
  useEffect(() => {
    const fetchAutoPilotStatus = async () => {
      try {
        setAutoPilotStatusLoading(true);
        const res = await apiClient.get("/playbook/status");
        setAutoPilotStatus(res.data);
      } catch (err) {
        console.log("Failed to fetch Auto-Pilot status");
      } finally {
        setAutoPilotStatusLoading(false);
      }
    };
    if (user) fetchAutoPilotStatus();
  }, [user]);
  
  // Check profile completeness for location wizard
  useEffect(() => {
    const checkProfileCompleteness = async () => {
      try {
        const res = await apiClient.get("/brand/profile-completeness");
        setProfileCompleteness(res.data);
        
        // Show location wizard if location is missing
        if (res.data.wizard_needed && res.data.missing_fields?.includes("location")) {
          setShowLocationWizard(true);
        }
      } catch (err) {
        console.log("Failed to check profile completeness");
      }
    };
    if (user) checkProfileCompleteness();
  }, [user]);
  
  // Handle location wizard submission
  const handleLocationWizardSubmit = async () => {
    if (!locationWizardData.city || !locationWizardData.country) {
      toast.error("Please enter at least city and country");
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append("city", locationWizardData.city);
      formData.append("country", locationWizardData.country);
      if (locationWizardData.region) {
        formData.append("region", locationWizardData.region);
      }
      
      await apiClient.post("/brand/update-location", formData);
      toast.success("Location saved! Your posts will now be geo-optimized.");
      setShowLocationWizard(false);
      
      // Refresh profile completeness
      const res = await apiClient.get("/brand/profile-completeness");
      setProfileCompleteness(res.data);
    } catch (error) {
      toast.error("Failed to save location");
      console.error(error);
    }
  };
  
  // Fetch content styles on mount
  useEffect(() => {
    const fetchContentStyles = async () => {
      try {
        const res = await apiClient.get("/settings/content-styles");
        setContentStyleOptions(res.data?.styles || {});
      } catch (err) {
        console.log("Failed to fetch content styles, using defaults");
        // Fallback defaults
        setContentStyleOptions({
          "professional": { name: "Professional", emoji: "🏢", best_for: ["Business", "Corporate"], description: "Formal, polished, fact-driven" },
          "promotional": { name: "Promotional", emoji: "📢", best_for: ["Marketing", "Sales"], description: "Exciting, persuasive, action-oriented" },
          "educational": { name: "Educational", emoji: "📚", best_for: ["Teachers", "How-to"], description: "Informative, clear, factual" },
          "inspirational": { name: "Inspirational", emoji: "✨", best_for: ["Coaches", "Motivational"], description: "Uplifting, emotional, empowering" },
          "casual": { name: "Casual / Fun", emoji: "😄", best_for: ["Gen-Z", "Entertainment"], description: "Relaxed, friendly, approachable" },
          "romantic": { name: "Romantic", emoji: "💕", best_for: ["Couples", "Weddings"], description: "Dreamy, atmospheric, intimate" },
          "luxury": { name: "Luxury", emoji: "💎", best_for: ["Premium brands", "High-end"], description: "Elegant, sophisticated, exclusive" },
          "energetic": { name: "Energetic", emoji: "⚡", best_for: ["Sports", "Fitness"], description: "Dynamic, powerful, high-energy" },
          "cultural": { name: "Cultural", emoji: "🌍", best_for: ["Regional", "Heritage"], description: "Authentic, traditional, culturally rich" },
          "documentary": { name: "Documentary", emoji: "🎬", best_for: ["Storytelling", "History"], description: "Narrative, cinematic, thoughtful" },
        });
      }
    };
    fetchContentStyles();
  }, []);
  
  // Step 3: Settings - Only show connected platforms
  const connectedPlatforms = useMemo(() => {
    // Check API data first, then fall back to user.social_connections
    const apiConnected = socialAccounts
      .filter(acc => acc.connected !== false)
      .map(acc => acc.platform);
    
    if (apiConnected.length > 0) {
      return apiConnected;
    }
    
    // Fallback to old format
    const connections = user?.social_connections || {};
    const connected = [];
    if (connections.facebook?.connected) connected.push("facebook");
    if (connections.instagram?.connected) connected.push("instagram");
    if (connections.tiktok?.connected) connected.push("tiktok");
    if (connections.pinterest?.connected) connected.push("pinterest");
    return connected;
  }, [user?.social_connections, socialAccounts]);
  
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  
  // Initialize selected platforms based on connections
  useEffect(() => {
    if (connectedPlatforms.length > 0) {
      setSelectedPlatforms(connectedPlatforms);
    }
  }, [connectedPlatforms]);
  
  const [postStyle, setPostStyle] = useState("generic"); // "generic" or "my_brand"
  const [brandingStyle, setBrandingStyle] = useState("subtle"); // "subtle" or "prominent"
  const [featureYourself, setFeatureYourself] = useState("none"); // "none", "subtle", or "prominent"
  const [savedFeatureYourself, setSavedFeatureYourself] = useState("none"); // Store previous selection for state restoration
  const [featureYourselfPosition, setFeatureYourselfPosition] = useState("bottom-left"); // Position for profile photo
  const [videoOverlayStyle, setVideoOverlayStyle] = useState("static"); // "static" or "animated"
  const [videoOverlayAnimation, setVideoOverlayAnimation] = useState("fade"); // fade, fly_in, fly_out, slide, zoom, wipe
  const [headshotSource, setHeadshotSource] = useState("profile"); // "profile" or "upload"
  const [headshotFile, setHeadshotFile] = useState(null);
  const [headshotPreview, setHeadshotPreview] = useState(null);
  const [contentLanguage, setContentLanguage] = useState("en");
  
  // Extended Video Selection (30s+)
  const [showExtendedVideoOptions, setShowExtendedVideoOptions] = useState(false);
  const [extendedVideoDuration, setExtendedVideoDuration] = useState(null); // 30, 40, 50, 60
  const [extendedVideoQuality, setExtendedVideoQuality] = useState("720p"); // 720p or 1080p
  
  // Branding
  const [selectedBrandPicture, setSelectedBrandPicture] = useState(null);
  const hasBrandAssets = user?.headshot_url || user?.logo_url || user?.contact_info?.phone1;
  
  // Listing Details (expanded categories)
  const [showListingDetails, setShowListingDetails] = useState(false);
  const [listingType, setListingType] = useState("property");
  const [listingDetails, setListingDetails] = useState({});
  const [fieldPositions, setFieldPositions] = useState({}); // For template field positioning
  const [textPosition, setTextPosition] = useState("bottom-center"); // Text overlay position
  
  // Video animation options (Phase 1)
  const [videoAnimations, setVideoAnimations] = useState({
    priceTag: false,      // Animated price tag sliding in
    lowerThird: false,    // Lower third name bar
    subtitles: true,      // Burned-in subtitles (default on)
  });
  
  // TikTok Settings (required for TikTok Content Sharing API compliance)
  const [tiktokSettings, setTiktokSettings] = useState({
    privacyLevel: "",
    allowComment: false,
    allowDuet: false,
    allowStitch: false,
    contentType: "original",
    isValid: false
  });
  const [tiktokConsented, setTiktokConsented] = useState(false);
  const isTikTokSelected = selectedPlatforms.includes("tiktok");
  const isPinterestSelected = selectedPlatforms.includes("pinterest");
  
  // One-time platform permissions consent tracking
  // This tracks if user has given blanket consent for TikTok/Pinterest auto-posting defaults
  const [platformPermissionsGranted, setPlatformPermissionsGranted] = useState({
    tiktok: null, // null = not checked yet, true = consented, false = needs consent
    pinterest: null
  });
  
  // Check if selected platforms that need consent have been granted
  const needsPlatformPermissions = () => {
    if (isTikTokSelected && platformPermissionsGranted.tiktok === false) return true;
    if (isPinterestSelected && platformPermissionsGranted.pinterest === false) return true;
    return false;
  };
  
  // Handler when permissions are completed
  const handlePlatformPermissionsComplete = (completedPlatforms) => {
    setPlatformPermissionsGranted(prev => {
      const updated = { ...prev };
      if (completedPlatforms.includes("tiktok")) updated.tiktok = true;
      if (completedPlatforms.includes("pinterest")) updated.pinterest = true;
      return updated;
    });
  };
  
  // Check platform consent status on mount/platform selection change
  useEffect(() => {
    const checkPlatformConsent = async () => {
      const updates = { ...platformPermissionsGranted };
      
      if (isTikTokSelected && platformPermissionsGranted.tiktok === null) {
        try {
          const res = await apiClient.get("/social/tiktok/auto-posting-settings");
          updates.tiktok = res.data?.auto_posting_consent === true;
        } catch (e) {
          updates.tiktok = false;
        }
      }
      
      if (isPinterestSelected && platformPermissionsGranted.pinterest === null) {
        try {
          const res = await apiClient.get("/social/pinterest/auto-posting-settings");
          updates.pinterest = res.data?.auto_posting_consent === true;
        } catch (e) {
          updates.pinterest = false;
        }
      }
      
      setPlatformPermissionsGranted(updates);
    };
    
    if (isTikTokSelected || isPinterestSelected) {
      checkPlatformConsent();
    }
  }, [isTikTokSelected, isPinterestSelected]);
  
  // Reset TikTok consent when TikTok is deselected
  useEffect(() => {
    if (!isTikTokSelected) {
      setTiktokConsented(false);
      setTiktokSettings(prev => ({ ...prev, isValid: false }));
    }
  }, [isTikTokSelected]);
  
  // Template System
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [designStyleExpanded, setDesignStyleExpanded] = useState(false); // Collapsed by default
  const [selectedTemplate, setSelectedTemplate] = useState(null); // null = AI Generated
  const [templateTextStyle, setTemplateTextStyle] = useState({
    fontFamily: "sans-serif",
    fontSize: "medium",
    fontColor: "#ffffff",
    backgroundColor: "rgba(0,0,0,0.7)",
    textAlign: "center"
  });
  
  // When template is selected, auto-switch to generic style (template doesn't use personal branding)
  // AND auto-expand design style section so user can customize their template
  // AND auto-fill template fields from listingDetails if available
  useEffect(() => {
    if (selectedTemplate) {
      setPostStyle("generic");
      setDesignStyleExpanded(true); // Auto-expand to show customization options
      
      // Auto-fill template fields from listingDetails
      // This prevents users from having to re-enter data they already provided
      if (listingDetails && Object.keys(listingDetails).length > 0) {
        const templateFields = selectedTemplate.fields || [];
        const autoFilledFields = {};
        let fieldsAutoFilled = 0;
        
        // Smart field mapping: listingDetails keys -> template field IDs
        const fieldMappings = {
          // Real Estate
          "address": ["address", "location", "property_address"],
          "price": ["price", "listing_price", "amount"],
          "beds": ["beds", "bedrooms", "bed_count"],
          "baths": ["baths", "bathrooms", "bath_count"],
          "sqft": ["sqft", "square_feet", "area", "size"],
          "propertyType": ["property_type", "type", "listing_type"],
          "description": ["description", "details", "property_description"],
          // Restaurant/Food
          "dishName": ["dish_name", "item_name", "name", "title"],
          "dishPrice": ["dish_price", "item_price", "price"],
          "ingredients": ["ingredients", "items"],
          // Automotive
          "make": ["make", "brand", "manufacturer"],
          "model": ["model", "vehicle_model"],
          "year": ["year", "model_year"],
          "mileage": ["mileage", "kilometers", "miles"],
          // General
          "title": ["title", "headline", "name"],
          "subtitle": ["subtitle", "tagline", "subheading"],
          "offer": ["offer", "discount", "deal", "promo"],
          "validUntil": ["valid_until", "expiry", "end_date"],
          "ctaText": ["cta_text", "button_text", "action"],
        };
        
        // Try to match listingDetails to template fields
        templateFields.forEach(field => {
          const fieldId = field.id || field.name;
          const fieldIdLower = fieldId?.toLowerCase() || "";
          
          // Check direct match first
          if (listingDetails[fieldId]) {
            autoFilledFields[fieldId] = listingDetails[fieldId];
            fieldsAutoFilled++;
            return;
          }
          
          // Check smart mappings
          for (const [listingKey, possibleFieldIds] of Object.entries(fieldMappings)) {
            if (listingDetails[listingKey] && possibleFieldIds.some(id => fieldIdLower.includes(id))) {
              autoFilledFields[fieldId] = listingDetails[listingKey];
              fieldsAutoFilled++;
              return;
            }
          }
        });
        
        // If we found matching fields, update listingDetails to include them
        if (fieldsAutoFilled > 0) {
          setListingDetails(prev => ({
            ...prev,
            ...autoFilledFields
          }));
          
          // Show a subtle toast to let user know fields were auto-filled
          toast.success(`Auto-filled ${fieldsAutoFilled} field${fieldsAutoFilled > 1 ? 's' : ''} from your previous entries`, {
            duration: 2000
          });
        }
      }
    }
  }, [selectedTemplate]);
  
  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState("");
  const [videoJobId, setVideoJobId] = useState(null);
  const [videoProvider, setVideoProvider] = useState(null);
  const [estimatedSeconds, setEstimatedSeconds] = useState(0);
  
  // Confirmation Preview State (NEW)
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [scrapedData, setScrapedData] = useState(null);
  const [voiceoverPreview, setVoiceoverPreview] = useState("");
  const [isScrapingUrl, setIsScrapingUrl] = useState(false);
  
  // Smart Content Detection State (AI-powered category detection)
  const [smartDetection, setSmartDetection] = useState(null);
  const [isAnalyzingContent, setIsAnalyzingContent] = useState(false);
  const [smartDetectionDismissed, setSmartDetectionDismissed] = useState(false);
  
  // Derived detected category from smart detection
  const detectedCategory = smartDetection?.detected_category || null;
  
  // Results
  const [generatedPosts, setGeneratedPosts] = useState([]);
  const [generatedVideo, setGeneratedVideo] = useState(null);
  
  // Resume from Saved Clip (P2 Feature)
  const [partialClips, setPartialClips] = useState([]);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [resumingVideoId, setResumingVideoId] = useState(null);
  
  // Credit pool state
  const [creditSummary, setCreditSummary] = useState(null);
  
  // Fetch credit summary from credit pool
  useEffect(() => {
    const fetchCreditSummary = async () => {
      try {
        const res = await apiClient.get("/users/credit-summary");
        setCreditSummary(res.data);
      } catch (err) {
        console.log("Failed to fetch credit summary");
      }
    };
    if (user) fetchCreditSummary();
  }, [user]);
  
  // Credits check - use credit pool if available
  const postsRemaining = creditSummary?.total_posts ?? user?.posts_remaining ?? 0;
  // Use videoCreditsInfo.plan if available (fresh from API), fallback to user.plan (from JWT)
  const userPlan = videoCreditsInfo?.plan || user?.plan || "free_trial";
  const isTrialUser = userPlan === "free_trial";
  const isStarterUser = userPlan === "starter" || userPlan === "starter_yearly";
  const isGrowthUser = userPlan === "growth" || userPlan === "growth_yearly";
  const isProUser = userPlan === "pro" || userPlan === "pro_yearly";
  // Note: Starter is a PAID plan with 50 posts, 0 video credits
  // Only block VIDEO features for starter (no credits), not all features
  const hasVideoCredits = user?.video_credits_remaining > 0 || isGrowthUser || isProUser;
  
  // Fetch partial clips on mount (for users with failed video generations)
  useEffect(() => {
    const fetchPartialClips = async () => {
      if (!user || isTrialUser) return; // Only block trial users from brand learning
      try {
        const response = await apiClient.get('/content/video/partial-clips');
        if (response.data && (response.data.partial_clips?.length > 0 || response.data.failed_jobs?.length > 0)) {
          setPartialClips([
            ...(response.data.partial_clips || []),
            ...(response.data.failed_jobs || [])
          ]);
        }
      } catch (error) {
        // Silent fail - this is optional functionality
        console.log("No partial clips to resume");
      }
    };
    fetchPartialClips();
  }, [user, isTrialUser]);
  
  // Resume a partial video generation
  const handleResumeVideo = async (videoId) => {
    setResumingVideoId(videoId);
    try {
      const response = await apiClient.post(`/content/video/resume/${videoId}`);
      if (response.data.success) {
        if (response.data.video_url) {
          // Completed immediately
          setGeneratedVideo({
            video_url: response.data.video_url,
            caption: response.data.caption
          });
          setStep(5);
          toast.success("Video resumed and completed!");
          // Remove from partial clips
          setPartialClips(partialClips.filter(c => c.video_id !== videoId));
        } else if (response.data.resumable) {
          // Need to re-generate - deduct credits
          toast.info(response.data.message);
        }
        setShowResumeModal(false);
      } else {
        toast.error(response.data.message || "Failed to resume video");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resume video");
    } finally {
      setResumingVideoId(null);
    }
  };
  
  // Handle URL query params for quick actions (e.g., /create?type=url)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const typeParam = params.get('type');
    if (typeParam) {
      // Map query param to input type
      const typeMap = {
        'url': 'url',
        'write': 'text',
        'text': 'text',
        'image': 'image',
        'photo': 'image',
        'video': 'video'
      };
      const mappedType = typeMap[typeParam.toLowerCase()];
      if (mappedType) {
        setInputType(mappedType);
        setStep(1); // Always start at Step 1
      }
    }
  }, [location.search]);
  
  // Scroll to top when step changes (Safari compatible)
  useEffect(() => {
    // Multiple scroll methods for cross-browser compatibility
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      // Scroll the anchor element into view
      const anchor = document.getElementById('create-page-top');
      if (anchor) {
        anchor.scrollIntoView({ behavior: 'instant', block: 'start' });
      }
    });
  }, [step]);
  
  // Auto-select "text" input when prefilling from Inspiration Hub or other pages
  useEffect(() => {
    if (prefillText) {
      setInputType("text");
      setInputText(prefillText);
      // Don't advance to step 2 - let user review the prefilled text first
    }
  }, []); // Only on mount
  
  // Check for saved draft on mount (if not prefilling from another page and not from Post Analyzer)
  // Shows a prompt to let user choose to resume or start fresh
  useEffect(() => {
    if (!prefillText && !fromViralSimulator && !fromPostAnalyzer) {
      const draft = getSavedDraft();
      if (draft && (draft.step > 1 || draft.inputType)) {
        // Show draft prompt instead of auto-restoring
        setSavedDraftData(draft);
        setShowDraftPrompt(true);
      }
    }
  }, []); // Only on mount
  
  // Resume draft - called when user clicks "Continue where I left off"
  const resumeDraft = () => {
    if (savedDraftData) {
      if (savedDraftData.step) setStep(savedDraftData.step);
      if (savedDraftData.inputType) setInputType(savedDraftData.inputType);
      if (savedDraftData.inputUrl) setInputUrl(savedDraftData.inputUrl);
      if (savedDraftData.inputText) setInputText(savedDraftData.inputText);
      if (savedDraftData.outputType) setOutputType(savedDraftData.outputType);
      if (savedDraftData.scheduleType) setScheduleType(savedDraftData.scheduleType);
      if (savedDraftData.days) setDays(savedDraftData.days);
      if (savedDraftData.postsPerDay) setPostsPerDay(savedDraftData.postsPerDay);
      if (savedDraftData.listingType) setListingType(savedDraftData.listingType);
      if (savedDraftData.listingDetails) setListingDetails(savedDraftData.listingDetails);
      if (savedDraftData.textPosition) setTextPosition(savedDraftData.textPosition);
      if (savedDraftData.selectedTemplate) setSelectedTemplate(savedDraftData.selectedTemplate);
      if (savedDraftData.fieldPositions) setFieldPositions(savedDraftData.fieldPositions);
      // Set Create Your Way mode since they have progress
      setShowCreateYourWay(true);
      toast.success("Resumed your previous work");
    }
    setShowDraftPrompt(false);
    setSavedDraftData(null);
  };
  
  // Discard draft - called when user clicks "Start Fresh"
  const discardDraft = () => {
    clearDraft();
    setShowDraftPrompt(false);
    setSavedDraftData(null);
    // User will see Auto-Pilot vs Create Your Way selection
  };
  
  // Save draft when navigating away (before unload or navigation)
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Only save if user has started (step > 1 or has input)
      if (step > 1 || inputType || inputText || inputUrl) {
        saveDraft({
          step,
          inputType,
          inputUrl,
          inputText,
          outputType,
          scheduleType,
          days,
          postsPerDay,
          listingType,
          listingDetails,
          textPosition,
          selectedTemplate,
          fieldPositions
        });
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [step, inputType, inputUrl, inputText, outputType, scheduleType, days, postsPerDay, listingType, listingDetails, textPosition, selectedTemplate, fieldPositions, saveDraft]);
  
  // Use a ref to track if we should save on unmount (prevents stale closure)
  const shouldSaveDraftRef = useRef(false);
  const draftDataRef = useRef({});
  
  // Update refs whenever draft-related state changes
  // IMPORTANT: Don't save draft when on step 5 (completion) - user finished successfully
  useEffect(() => {
    shouldSaveDraftRef.current = step >= 2 && step < 5 && (!!inputType || !!inputText || !!inputUrl);
    draftDataRef.current = {
      step,
      inputType,
      inputUrl,
      inputText,
      outputType,
      scheduleType,
      days,
      postsPerDay,
      listingType,
      listingDetails,
      textPosition,
      selectedTemplate,
      fieldPositions
    };
  }, [step, inputType, inputUrl, inputText, outputType, scheduleType, days, postsPerDay, listingType, listingDetails, textPosition, selectedTemplate, fieldPositions]);
  
  // Save draft on component unmount (SPA navigation)
  useEffect(() => {
    return () => {
      // Cleanup function runs when component unmounts
      if (shouldSaveDraftRef.current) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          data: draftDataRef.current,
          timestamp: Date.now()
        }));
      }
    };
  }, []); // Empty deps = only runs on unmount
  
  // Clear draft when generation completes (step 5)
  useEffect(() => {
    if (step === 5) {
      clearDraft();
    }
  }, [step, clearDraft]);
  
  // Video credit costs mapping
  const VIDEO_CREDIT_COSTS = {
    "10s_720p": 1,
    "20s_720p": 2,
    "20s_1080p": 4,
    "30s_1080p": 6,
  };
  
  // Plan video access restrictions
  // NOTE: 30s+ Extended Videos are ONLY available via Pay As You Go packs, not in any subscription plan
  const PLAN_VIDEO_ACCESS = {
    "free_trial": null,
    "starter": null,
    "starter_yearly": null,
    "growth": { quality: "720p", durations: ["10s", "20s"] },
    "growth_yearly": { quality: "720p", durations: ["10s", "20s"] },
    "pro": { quality: "1080p", durations: ["20s"] },
    "pro_yearly": { quality: "1080p", durations: ["20s"] },
  };
  
  // Get current video type key
  const getVideoTypeKey = useCallback(() => {
    return `${videoDuration}s_${videoQuality}`;
  }, [videoDuration, videoQuality]);
  
  // Get credits needed for current video selection
  const getVideoCreditsNeededForSelection = useCallback(() => {
    const key = getVideoTypeKey();
    return VIDEO_CREDIT_COSTS[key] || 1;
  }, [getVideoTypeKey]);
  
  // Fetch video credits info
  useEffect(() => {
    const fetchVideoCredits = async () => {
      if (!user) return;
      try {
        const response = await apiClient.get('/users/video-credits');
        setVideoCreditsInfo(response.data);
      } catch (error) {
        console.error("Failed to fetch video credits:", error);
      }
    };
    fetchVideoCredits();
  }, [user]);
  
  // Get total video credits available - prefer credit pool data
  const totalVideoCredits = creditSummary?.total_video_credits ?? 
    videoCreditsInfo?.total_credits ?? 
    ((user?.video_credits_remaining || 0) + (user?.video_credits_payg || 0));
  
  // Calculate required credits based on use case
  const totalPosts = scheduleType === "bulk" ? days * postsPerDay : 1;
  
  // Credits calculation logic:
  // - Video input → Post output: POST credits (user's video already exists, we just create posts)
  // - Image/URL/Text → Video output: VIDEO credits (we're generating video content)
  // - Any → Post output (image): POST credits per day
  
  const getVideoCreditsNeeded = () => {
    if (outputType !== "video") return 0;
    if (inputType === "video") return 0; // Video input = just posting user's video, no generation
    // Calculate credits based on selected duration/quality
    const creditsPerVideo = getVideoCreditsNeededForSelection();
    return creditsPerVideo * totalPosts;
  };
  
  const getPostCreditsNeeded = () => {
    if (outputType === "video") {
      if (inputType === "video") return totalPosts; // Video input = post credits only
      return 0; // Image/URL/Text → Video = video credits only, no post credits
    }
    return totalPosts; // Post output = post credits
  };
  
  const totalCreditsNeeded = getPostCreditsNeeded();
  const totalVideoCreditsNeeded = getVideoCreditsNeeded();
  const hasEnoughPostCredits = postsRemaining >= totalCreditsNeeded;
  const hasEnoughVideoCredits = totalVideoCredits >= totalVideoCreditsNeeded;
  
  // Check plan access for selected video type
  const checkPlanVideoAccess = useCallback(() => {
    const planAccess = PLAN_VIDEO_ACCESS[userPlan];
    if (!planAccess) {
      return { allowed: false, reason: "no_video_access", message: "Upgrade to Growth or Pro to create videos" };
    }
    
    const durationStr = `${videoDuration}s`;
    if (!planAccess.durations.includes(durationStr)) {
      return { 
        allowed: false, 
        reason: "duration_not_allowed", 
        message: `${durationStr} videos are not available on your plan`,
        suggestedPlan: videoDuration === 30 ? "pro" : "growth"
      };
    }
    
    if (videoQuality === "1080p" && planAccess.quality !== "1080p") {
      return { 
        allowed: false, 
        reason: "hd_requires_pro", 
        message: "HD videos (1080p) require Pro plan",
        suggestedPlan: "pro"
      };
    }
    
    return { allowed: true };
  }, [userPlan, videoDuration, videoQuality]);
  
  // Get available durations for current plan
  const getAvailableDurations = useCallback(() => {
    const planAccess = PLAN_VIDEO_ACCESS[userPlan];
    if (!planAccess) return [];
    return planAccess.durations;
  }, [userPlan]);
  
  // Get available quality options for current plan
  const getAvailableQualities = useCallback(() => {
    const planAccess = PLAN_VIDEO_ACCESS[userPlan];
    if (!planAccess) return [];
    if (planAccess.quality === "1080p") return ["1080p"]; // Pro only gets HD
    return ["720p"]; // Growth gets 720p
  }, [userPlan]);
  
  // Update video settings when plan changes
  useEffect(() => {
    if (outputType === "video" && hasVideoCredits) {
      const availableDurations = getAvailableDurations();
      const availableQualities = getAvailableQualities();
      
      // Set defaults based on plan
      if (isProUser) {
        setVideoDuration(20);
        setVideoQuality("1080p");
      } else if (isGrowthUser) {
        setVideoDuration(10);
        setVideoQuality("720p");
      }
    }
  }, [outputType, userPlan, hasVideoCredits, isProUser, isGrowthUser, getAvailableDurations, getAvailableQualities]);
  
  // Fetch image source recommendation when content/category changes
  const fetchImageSourceRecommendation = useCallback(async (content, category) => {
    if (!content || outputType !== "post") return;
    
    try {
      const response = await apiClient.post('/content/image-source/recommend', {
        content: content,
        detected_category: category,
        user_preference: imageSource
      });
      setImageSourceRecommendation(response.data);
      
      // Auto-set image source based on high confidence recommendation
      if (!imageSource && response.data.confidence === "high") {
        setImageSource(response.data.recommended_source);
      }
    } catch (error) {
      console.error("Failed to get image source recommendation:", error);
    }
  }, [outputType, imageSource]);
  
  // Search stock photos
  const searchStockPhotos = useCallback(async (query) => {
    if (!query) return;
    
    setStockPhotoSearching(true);
    try {
      const response = await apiClient.post('/content/stock-photos/search', {
        query: query,
        count: 8,
        orientation: "landscape",
        content: inputText || inputUrl || ""
      });
      setStockPhotoResults(response.data.photos || []);
    } catch (error) {
      console.error("Failed to search stock photos:", error);
      // Silently fail - don't show error toast as user is just selecting image type
      // The AI will handle photo selection during generation if no photo is selected
      setStockPhotoResults([]);
    } finally {
      setStockPhotoSearching(false);
    }
  }, [inputText, inputUrl]);
  
  // Auto-fetch recommendation when detected category changes
  useEffect(() => {
    if (detectedCategory && outputType === "post" && (inputText || inputUrl)) {
      const content = inputText || inputUrl || "";
      fetchImageSourceRecommendation(content, detectedCategory);
    }
  }, [detectedCategory, outputType, inputText, inputUrl, fetchImageSourceRecommendation]);

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");
    
    if (inputType === "image" && !isImage) {
      toast.error("Please upload an image file");
      return;
    }
    if (inputType === "video" && !isVideo) {
      toast.error("Please upload a video file");
      return;
    }
    
    setInputFile(file);
    
    // Create preview
    if (isImage) {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview(e.target.result);
      reader.readAsDataURL(file);
    } else if (isVideo) {
      setFilePreview(URL.createObjectURL(file));
    }
  };
  
  // Clear file
  const clearFile = () => {
    setInputFile(null);
    setFilePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  
  // Check if Step 1 is complete
  const isStep1Complete = () => {
    if (!inputType) return false;
    if (inputType === "url" && !inputUrl.trim()) return false;
    if (inputType === "text" && !inputText.trim()) return false;
    if (inputType === "image" && !inputFile && inputFiles.length === 0) return false;
    if (inputType === "video" && !inputFile) return false;
    return true;
  };
  
  // Check if Step 2 is complete
  const isStep2Complete = () => {
    if (!outputType) return false;
    
    // For bulk modes with different templates, check all days have templates selected
    if (scheduleType === "bulk" && days > 1) {
      if (bulkContentMode === "same_content_different_template" || bulkContentMode === "different_content_different_template") {
        // Day 1 uses main template (selectedTemplate) or AI Generated
        // Days 2-N must have templates selected in bulkDayDetails
        for (let day = 2; day <= days; day++) {
          const dayData = bulkDayDetails[day];
          if (!dayData?.template) {
            return false; // Missing template for this day
          }
        }
      }
    }
    
    return true;
  };
  
  // Check if Step 3 is complete
  const isStep3Complete = () => {
    // If no platforms connected, still allow to proceed but show message
    if (connectedPlatforms.length === 0) {
      return false; // Block generation until they connect at least one
    }
    // If TikTok or Pinterest is selected but needs permissions, block
    if (needsPlatformPermissions()) {
      return false;
    }
    // If TikTok is selected, require consent (per-post settings valid)
    if (selectedPlatforms.includes("tiktok") && !tiktokConsented) {
      return false;
    }
    return selectedPlatforms.length > 0;
  };
  
  // Determine available output types based on input
  const getAvailableOutputs = () => {
    // Video input: only post option (share with AI captions)
    if (inputType === "video") {
      return [{ id: "post", name: "Post Your Video", desc: "Share your video with AI captions & hashtags", credits: totalPosts }];
    }
    
    // Check if user can create videos
    const canCreateVideos = isGrowthUser || isProUser;
    const creditsPerVideo = getVideoCreditsNeededForSelection();
    
    // Get plan-appropriate default display for video card
    const getVideoCardDescription = () => {
      if (!canCreateVideos) return "AI video with voiceover";
      if (isProUser) return "AI video with voiceover (20s 720p/1080p)";
      if (isGrowthUser) return "AI video with voiceover (10s/20s 720p)";
      return "AI video with voiceover";
    };
    
    // All other inputs can create posts or AI videos
    return [
      { id: "post", name: "Picture Post", desc: "AI-generated image + caption", credits: totalPosts },
      { 
        id: "video", 
        name: "Video", 
        desc: outputType === "video" 
          ? `AI video with voiceover (${videoDuration}s ${videoQuality})` // Show actual selection when selected
          : getVideoCardDescription(), // Show plan defaults when not selected
        credits: creditsPerVideo, 
        isVideo: true,
        locked: isTrialUser || !hasVideoCredits
      }
    ];
  };
  
  // Poll for video status
  useEffect(() => {
    if (!videoJobId) return;
    
    const pollStatus = async () => {
      try {
        const response = await apiClient.get(`/content/video/status/${videoJobId}`);
        const { status, progress, progress_message, video_url, social_caption, provider, estimated_seconds } = response.data;
        
        setGenerationProgress(progress || 0);
        if (provider) setVideoProvider(provider);
        if (estimated_seconds) setEstimatedSeconds(estimated_seconds);
        if (progress_message) setProgressMessage(progress_message);
        
        if (status === "completed" && video_url) {
          setIsGenerating(false);
          setVideoJobId(null);
          setGeneratedVideo({
            video_url: video_url,
            caption: social_caption
          });
          setStep(5);
          toast.success("Video generated successfully!");
          refreshUser();
        } else if (status === "failed") {
          setIsGenerating(false);
          setVideoJobId(null);
          const errorMsg = response.data.error || "Video generation failed";
          toast.error(errorMsg.substring(0, 100));
        }
      } catch (error) {
        console.error("Poll error:", error);
      }
    };
    
    const interval = setInterval(pollStatus, 3000);
    return () => clearInterval(interval);
  }, [videoJobId, refreshUser]);
  
  // Scrape URL for preview
  const handleScrapePreview = async () => {
    if (!inputUrl.trim()) return;
    
    setIsScrapingUrl(true);
    try {
      const response = await apiClient.post('/content/scrape-preview', {
        url: inputUrl,
        output_type: outputType || 'image'
      });
      
      if (response.data.success) {
        setScrapedData(response.data.scraped_data);
        if (outputType === 'video' && response.data.suggested_voiceover) {
          setVoiceoverPreview(response.data.suggested_voiceover);
        }
        setShowConfirmation(true);
      } else {
        toast.error("Could not extract data from URL. Generation will use AI instead.");
        // Skip confirmation - just generate
        handleGenerate();
      }
    } catch (error) {
      console.error("Scrape error:", error);
      toast.error("URL preview unavailable. Proceeding with AI generation.");
      // Skip confirmation - just generate directly
      handleGenerate();
    } finally {
      setIsScrapingUrl(false);
    }
  };
  
  // Get voiceover preview from listing details
  const handleVoiceoverPreview = async () => {
    if (!listingType || Object.keys(listingDetails).length === 0) return;
    
    try {
      const response = await apiClient.post('/content/voiceover-preview', {
        listing_type: listingType,
        listing_details: listingDetails,
        duration: videoDuration
      });
      
      if (response.data.success) {
        setVoiceoverPreview(response.data.voiceover_script);
        if (response.data.was_trimmed) {
          toast.info(`Script trimmed to ${response.data.word_count} words to fit ${videoDuration}s video`);
        }
      }
    } catch (error) {
      console.error("Voiceover preview error:", error);
    }
  };

  // Generate template preview (viral score, caption suggestions) without using credits
  const [templatePreview, setTemplatePreview] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  
  const handleTemplatePreview = async () => {
    setIsLoadingPreview(true);
    try {
      // Determine scenario based on input/output types
      let scenario = "single_text";
      if (inputType === 'url') scenario = "single_url";
      else if (inputType === 'images' && uploadedFiles.length > 1) scenario = "single_multi_image";
      else if (inputType === 'images') scenario = "single_image";
      
      if (scheduleType !== 'single' && outputType !== 'video') {
        scenario = `bulk_${scenario.replace('single_', '')}`;
      }
      
      const previewPayload = {
        scenario,
        platforms: selectedPlatforms,
        template_id: selectedTemplate?.id || "ai_generated",
        url: inputType === 'url' ? inputUrl : null,
        text: inputType === 'text' ? userText : null,
        voice_transcript: voiceTranscript || null,
        image_urls: inputType === 'images' ? uploadedFiles.map(f => f.url || f.preview) : [],
        listing_type: showListingDetails ? listingType : null,
        listing_details: showListingDetails ? listingDetails : null,
        collage_layout: collageLayout,
        custom_fields: {
          days: scheduleType === 'bulk' ? selectedDays.length : 1,
          include_video: outputType === 'video',
          language: selectedLanguage
        }
      };
      
      const response = await apiClient.post('/preview/generate', previewPayload);
      setTemplatePreview(response.data);
      return response.data;
    } catch (error) {
      console.error("Template preview error:", error);
      return null;
    } finally {
      setIsLoadingPreview(false);
    }
  };
  
  // Smart Content Detection - Analyze user input to suggest category
  const analyzeContentForSmartDetection = async () => {
    // Skip if already analyzed
    if (smartDetection) {
      setIsAnalyzingContent(false);
      return;
    }
    
    try {
      const payload = {
        text: inputType === "text" ? inputText : null,
        voice_transcript: null, // Will be set if voice input was used
        image_base64: null,
        image_url: null
      };
      
      // For image input, convert first image to base64 for analysis
      if (inputType === "image" && (inputFile || inputFiles.length > 0)) {
        const fileToAnalyze = inputFile || inputFiles[0];
        try {
          const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const result = reader.result;
              // Extract base64 data without the data URL prefix
              const base64Data = result.split(',')[1];
              resolve(base64Data);
            };
            reader.onerror = reject;
            reader.readAsDataURL(fileToAnalyze);
          });
          payload.image_base64 = base64;
        } catch (imgErr) {
          console.error("Failed to convert image for analysis:", imgErr);
        }
      }
      
      // For URL input, just pass the text context (actual analysis happens server-side)
      if (inputType === "url" && inputUrl) {
        payload.text = inputUrl;
      }
      
      const response = await apiClient.post("/content/analyze", payload);
      
      if (response.data.success) {
        setSmartDetection(response.data);
        // Auto-set listing type based on detection
        if (response.data.detected_category && response.data.confidence > 0.6) {
          const categoryMap = {
            "property": "property",
            "real_estate": "property",
            "product": "product",
            "ecommerce": "product",
            "restaurant": "restaurant",
            "service": "service",
            "event": "event",
            "events_celebrations": "event",
            "job": "job",
            "automotive": "automotive",
            "fitness": "fitness",
            "travel": "travel",
            "nature": "travel",
            "education": "education",
            "pet": "pet",
            "rental": "rental"
          };
          const mappedCategory = categoryMap[response.data.detected_category];
          if (mappedCategory) {
            setListingType(mappedCategory);
          }
        }
      }
    } catch (err) {
      console.error("Smart detection error:", err);
      // Silent fail - don't block user flow
    } finally {
      setIsAnalyzingContent(false);
    }
  };
  
  // Handle step 1 to step 2 transition with smart detection
  const handleStep1Continue = () => {
    // Set analyzing state BEFORE navigating to show loading indicator immediately
    setIsAnalyzingContent(true);
    setStep(2);
    // Then run the actual analysis
    analyzeContentForSmartDetection();
  };
  
  // Check if confirmation is needed before generation
  const needsConfirmation = () => {
    // URL input always needs confirmation (to show scraped data)
    if (inputType === 'url' && inputUrl.trim()) return true;
    // Video output ALWAYS needs confirmation (to show voiceover script options)
    if (outputType === 'video') return true;
    // Template selection always shows preview
    if (selectedTemplate && selectedTemplate.id !== 'ai_generated') return true;
    return false;
  };
  
  // Handle the confirm and generate flow
  const handleConfirmAndGenerate = async () => {
    if (needsConfirmation() && !showConfirmation) {
      // First, get preview data
      if (inputType === 'url') {
        await handleScrapePreview();
      } else if (outputType === 'video') {
        // For video outputs, always show voiceover options
        if (showListingDetails && Object.keys(listingDetails).length > 0) {
          await handleVoiceoverPreview();
        }
        // Show confirmation modal with voiceover script editor
        setShowConfirmation(true);
      } else {
        // For template-based or other cases, get template preview
        await handleTemplatePreview();
        setShowConfirmation(true);
      }
    } else {
      // Already confirmed or no confirmation needed - proceed to generate
      setShowConfirmation(false);
      handleGenerate();
    }
  };
  
  // Generate content
  const handleGenerate = async () => {
    if (outputType === "video") {
      // Check trial/starter restriction
      if (isTrialUser || !hasVideoCredits) {
        toast.error("Video creation requires Growth or Pro plan. Upgrade to create videos!");
        return;
      }
      
      // Check plan access for selected video type
      const accessCheck = checkPlanVideoAccess();
      if (!accessCheck.allowed) {
        toast.error(accessCheck.message);
        return;
      }
      
      // Check credits
      if (!hasEnoughVideoCredits) {
        // Fetch insufficient credits options from API
        try {
          const videoType = getVideoTypeKey();
          const response = await apiClient.get(`/users/video-credits/options/${videoType}`);
          setInsufficientCreditsOptions(response.data);
          setShowVideoCreditsModal(true);
        } catch (error) {
          toast.error("Not enough video credits");
        }
        return;
      }
      await generateVideo();
    } else {
      if (!hasEnoughPostCredits) {
        // Show modal for insufficient post credits
        const needed = totalCreditsNeeded - postsRemaining;
        setPostCreditsNeeded(needed);
        setShowPostCreditsModal(true);
        return;
      }
      await generatePosts();
    }
  };
  
  // Generate video
  const generateVideo = async () => {
    setIsGenerating(true);
    setGenerationProgress(0);
    setStep(4);
    
    try {
      const formData = new FormData();
      formData.append("language", contentLanguage);
      formData.append("style", postStyle);
      formData.append("duration", videoDuration.toString());
      formData.append("quality", videoQuality);
      formData.append("video_type", getVideoTypeKey()); // e.g., "20s_1080p"
      formData.append("platforms", JSON.stringify(selectedPlatforms));
      
      if (inputType === "url") {
        // Normalize URL - add https:// if missing
        let url = inputUrl;
        if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
          url = `https://${url}`;
        }
        formData.append("source_url", url);
      } else if (inputType === "text") {
        formData.append("prompt", inputText);
      } else if (inputType === "image" && inputFile) {
        formData.append("image_file", inputFile);
        // Multi-image support for video
        if (inputFiles.length >= 2) {
          inputFiles.forEach((file) => {
            formData.append("image_files", file);
          });
          formData.append("hero_index", collageHeroIndex.toString());
          formData.append("image_transition", collageTransition);
        }
      }
      
      // Listing Details (for real estate, products, services, etc.)
      if (showListingDetails && Object.keys(listingDetails).length > 0) {
        formData.append("listing_type", listingType);
        formData.append("listing_details", JSON.stringify(listingDetails));
      }
      
      // User-confirmed voiceover script (from preview)
      if (voiceoverPreview && voiceoverPreview.trim()) {
        formData.append("audio_script", voiceoverPreview);
      }
      
      // Include audio by default for videos
      formData.append("include_audio", "true");
      formData.append("audio_voice", selectedVoice);
      
      // Branding style (subtle or prominent) - ONLY for my_brand post style
      if (postStyle === "my_brand" && brandingStyle && brandingStyle !== "none") {
        formData.append("branding_style", brandingStyle);
      }
      
      // Feature yourself - headshot overlay on video
      if (featureYourself && featureYourself !== "none") {
        formData.append("feature_yourself", featureYourself);
        formData.append("feature_yourself_position", featureYourselfPosition);
      }
      
      // Video overlay style (static vs animated text)
      formData.append("video_overlay_style", videoOverlayStyle);
      if (videoOverlayStyle === "animated") {
        formData.append("video_overlay_animation", videoOverlayAnimation);
      }
      
      // Video animation options (Phase 1)
      formData.append("animations", JSON.stringify(videoAnimations));
      
      // Content Style (mandatory) - affects voiceover, music, image style
      formData.append("content_style", contentStyle);
      
      // Input context (user's intent for URL/Image inputs)
      if (inputContext && inputContext.trim()) {
        formData.append("input_context", inputContext.trim());
      }
      
      const response = await apiClient.post("/content/video/generate", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      if (response.data.video_id) {
        setVideoJobId(response.data.video_id);
        setVideoProvider(response.data.provider);
        setEstimatedSeconds(response.data.estimated_seconds);
        toast.info(`Video generation started (${response.data.provider})`);
      }
    } catch (error) {
      setIsGenerating(false);
      const message = error.response?.data?.detail || "Video generation failed";
      toast.error(message);
    }
  };
  
  // Generate posts
  const generatePosts = async () => {
    setIsGenerating(true);
    setGenerationProgress(0);
    setStep(4);
    
    try {
      const formData = new FormData();
      formData.append("platforms", JSON.stringify(selectedPlatforms));
      formData.append("language", contentLanguage);
      formData.append("style", postStyle);
      
      if (inputType === "url") {
        // Normalize URL - add https:// if missing
        let url = inputUrl;
        if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
          url = `https://${url}`;
        }
        formData.append("source_url", url);
        formData.append("url_intent", urlIntent); // Phase 3: URL intent
      } else if (inputType === "text") {
        formData.append("text_content", inputText);
      } else if (inputType === "image") {
        // Support multiple images
        if (inputFiles.length > 0) {
          inputFiles.forEach((file, index) => {
            formData.append("image_files", file);
          });
          // Multi-image collage settings
          if (inputFiles.length >= 2) {
            formData.append("hero_index", collageHeroIndex.toString());
            formData.append("collage_layout", collageLayout);
            formData.append("use_collage", "true");
          }
        } else if (inputFile) {
          formData.append("image_file", inputFile);
        }
      } else if (inputType === "video" && inputFile) {
        formData.append("video_file", inputFile);
      }
      
      // Branding
      if (postStyle === "my_brand" && selectedBrandPicture) {
        formData.append("brand_picture_type", selectedBrandPicture);
      }
      
      // Listing Details (for real estate, products, etc.)
      if (showListingDetails && Object.keys(listingDetails).length > 0) {
        formData.append("listing_type", listingType);
        formData.append("listing_details", JSON.stringify(listingDetails));
        formData.append("text_position", textPosition);
      }
      
      // Branding style - ONLY send when my_brand style is selected
      if (postStyle === "my_brand" && brandingStyle && brandingStyle !== "none") {
        formData.append("branding_style", brandingStyle);
      }
      
      // Feature yourself for posts (headshot overlay)
      if (featureYourself && featureYourself !== "none") {
        formData.append("feature_yourself", featureYourself);
        formData.append("feature_yourself_position", featureYourselfPosition);
      }
      
      // Template (if selected)
      if (selectedTemplate) {
        formData.append("template_id", selectedTemplate.id);
        // Send field positions for template
        if (Object.keys(fieldPositions).length > 0) {
          formData.append("field_positions", JSON.stringify(fieldPositions));
        }
        // Send text style customization
        if (templateTextStyle && Object.keys(templateTextStyle).length > 0) {
          formData.append("text_style", JSON.stringify(templateTextStyle));
        }
      }
      
      let endpoint = "/content/generate-single";
      
      // Use collage endpoint for multiple images
      if (inputType === "image" && inputFiles.length >= 2 && scheduleType !== "bulk") {
        endpoint = "/content/generate-collage";
      } else if (scheduleType === "bulk") {
        formData.append("days", days.toString());
        formData.append("posts_per_day", postsPerDay.toString());
        formData.append("unique_images", "true"); // Always unique - default behavior
        formData.append("output_type", outputType); // "post" or "video"
        
        // For image→video bulk, send the mode (same vs inspired)
        if (inputType === "image" && outputType === "video") {
          formData.append("bulk_video_mode", bulkVideoMode); // "same" or "inspired"
        } else if (inputType !== "video") {
          // URL/Text → Video: always generate unique videos
          formData.append("bulk_video_mode", "inspired");
        } else {
          // Video input: same video, different posts
          formData.append("bulk_video_mode", "same");
        }
        
        // For image→post bulk: same_image vs inspired_images
        if (inputType === "image" && outputType === "post") {
          formData.append("bulk_image_mode", bulkImageMode); // "same_image" or "inspired_images"
        }
        
        // For content variation mode (same_content_same_template, etc.)
        formData.append("bulk_content_mode", bulkContentMode);
        
        // Send per-day customization data if using different content/templates mode
        if (bulkContentMode !== "same_content_same_template" && Object.keys(bulkDayDetails).length > 0) {
          formData.append("bulk_day_details", JSON.stringify(bulkDayDetails));
        }
        
        // Enable async mode for larger bulk generations (7+ days) to avoid timeouts
        if (days >= 7) {
          formData.append("async_mode", "true");
        }
        
        endpoint = "/content/generate-bulk";
      }
      
      // Content Style (mandatory) - affects image generation prompts, caption style
      formData.append("content_style", contentStyle);
      
      // Image Source selection for Picture Posts
      if (outputType === "post" && imageSource) {
        formData.append("image_source", imageSource); // "stock" or "ai"
        
        // Include selected stock photo if available
        if (imageSource === "stock" && selectedStockPhoto) {
          formData.append("stock_photo_url", selectedStockPhoto.url_regular || selectedStockPhoto.url_full);
          formData.append("stock_photo_id", selectedStockPhoto.id);
          formData.append("stock_photo_photographer", selectedStockPhoto.photographer || "");
        }
        
        // Smart Image Mode - only for AI Generated images
        if (imageSource === "ai" && smartImageMode) {
          formData.append("smart_image_mode", smartImageMode); // "auto", "abstract", or "lifestyle"
        }
      }
      
      // Input context (user's intent for URL/Image inputs)
      if (inputContext && inputContext.trim()) {
        formData.append("input_context", inputContext.trim());
      }
      
      // TikTok Settings (required for TikTok API compliance)
      if (selectedPlatforms.includes("tiktok") && tiktokSettings) {
        formData.append("tiktok_settings", JSON.stringify({
          privacy_level: tiktokSettings.privacyLevel,
          allow_comment: tiktokSettings.allowComment,
          allow_duet: tiktokSettings.allowDuet,
          allow_stitch: tiktokSettings.allowStitch,
          content_type: tiktokSettings.contentType
        }));
      }
      
      const response = await apiClient.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setGenerationProgress(Math.round((e.loaded / e.total) * 30));
        }
      });
      
      // Handle async bulk generation - poll for status
      if (response.data.async && response.data.task_id) {
        const taskId = response.data.task_id;
        setProgressMessage("Processing in background to avoid timeouts...");
        
        // Poll for completion
        let attempts = 0;
        const maxAttempts = 120; // 10 minutes max (5 seconds per poll)
        
        const pollStatus = async () => {
          try {
            const statusRes = await apiClient.get(`/content/bulk-status/${taskId}`);
            const status = statusRes.data;
            
            if (status.status === "completed" && status.result) {
              setGenerationProgress(100);
              setIsGenerating(false);
              setGeneratedPosts(status.result.posts || []);
              setStep(5);
              toast.success(`${status.result.posts?.length || 0} posts created!`);
              refreshUser();
              return;
            } else if (status.status === "failed") {
              setIsGenerating(false);
              toast.error(status.error || "Generation failed");
              return;
            } else if (status.status === "processing") {
              setGenerationProgress(30 + Math.min(status.progress || 0, 60));
              setProgressMessage(`Generating day ${Math.floor((status.progress / 100) * days)} of ${days}...`);
            }
            
            attempts++;
            if (attempts < maxAttempts) {
              setTimeout(pollStatus, 5000); // Poll every 5 seconds
            } else {
              setIsGenerating(false);
              toast.error("Generation timed out. Check your posts in the dashboard.");
            }
          } catch (pollError) {
            attempts++;
            if (attempts < maxAttempts) {
              setTimeout(pollStatus, 5000);
            } else {
              setIsGenerating(false);
              toast.error("Could not get generation status");
            }
          }
        };
        
        setTimeout(pollStatus, 3000); // Start polling after 3 seconds
        return;
      }
      
      setGenerationProgress(100);
      setIsGenerating(false);
      
      // Parse posts from response - handle different response structures
      let posts = [];
      if (response.data.posts && Array.isArray(response.data.posts) && response.data.posts.length > 0) {
        posts = response.data.posts;
      } else if (response.data.id || response.data.post_id) {
        // Single post response - wrap in array
        posts = [response.data];
      }
      
      // Check if any posts were actually created
      if (posts.length === 0) {
        toast.error("Post generation failed. Please try again.");
        setStep(3); // Go back to step 3
        return;
      }
      
      setGeneratedPosts(posts);
      
      // Store the first post ID for navigation - use actual post ID, NOT content_id
      const firstPostId = posts[0]?.id || posts[0]?.post_id;
      console.log('Generated posts:', posts);
      console.log('First post ID for navigation:', firstPostId);
      if (firstPostId) {
        sessionStorage.setItem('viraly_last_created_post', firstPostId);
      }
      
      setStep(5);
      toast.success(`${posts.length || 1} post(s) created!`);
      
      // Mark content as created for UI guidance glow
      if (markContentCreated) markContentCreated();
      
      refreshUser();
    } catch (error) {
      setIsGenerating(false);
      toast.error(getErrorMessage(error, "Generation failed"));
    }
  };
  
  // Progress display for video
  const getProgressMessage = () => {
    if (outputType === "video") {
      // Use the progress message from the backend if available
      if (progressMessage) {
        return progressMessage;
      }
      // Fallback messages if no backend message
      const provider = videoProvider || "AI";
      if (generationProgress < 10) {
        return `Initializing ${provider} video generation...`;
      } else if (generationProgress < 30) {
        return `Analyzing your content with ${provider}...`;
      } else if (generationProgress < 60) {
        return `Creating visuals with ${provider}... This may take 2-3 minutes`;
      } else if (generationProgress < 90) {
        return `Finalizing video with ${provider}... Almost there!`;
      } else {
        return `Applying finishing touches...`;
      }
    }
    if (generationProgress < 30) {
      return "Understanding your content...";
    } else if (generationProgress < 60) {
      return "Generating engaging copy...";
    } else if (generationProgress < 90) {
      return "Optimizing for engagement...";
    }
    return "Finalizing your posts...";
  };

  // Ref for scroll anchor
  const scrollAnchorRef = useRef(null);
  
  return (
    <DashboardLayout>
      {/* Scroll anchor at very top */}
      <div ref={scrollAnchorRef} id="create-page-top" />
      
      {/* Location Wizard Modal */}
      {showLocationWizard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-viraly-dark border border-white/10 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <Globe className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold text-white">Where is your business?</h3>
                <p className="text-xs text-viraly-muted">This helps us create geo-optimized content</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-viraly-muted mb-1 block">City *</label>
                <input
                  type="text"
                  placeholder="e.g., Berlin, Tokyo, Sydney"
                  value={locationWizardData.city}
                  onChange={(e) => setLocationWizardData({...locationWizardData, city: e.target.value})}
                  className="input-base w-full"
                />
              </div>
              
              <div>
                <label className="text-sm text-viraly-muted mb-1 block">Country *</label>
                <input
                  type="text"
                  placeholder="e.g., Germany, Japan, Australia"
                  value={locationWizardData.country}
                  onChange={(e) => setLocationWizardData({...locationWizardData, country: e.target.value})}
                  className="input-base w-full"
                />
              </div>
              
              <div>
                <label className="text-sm text-viraly-muted mb-1 block">State/Province (optional)</label>
                <input
                  type="text"
                  placeholder="e.g., Bavaria, Tokyo Prefecture, NSW"
                  value={locationWizardData.region}
                  onChange={(e) => setLocationWizardData({...locationWizardData, region: e.target.value})}
                  className="input-base w-full"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowLocationWizard(false)}
              >
                Skip for now
              </Button>
              <Button
                className="flex-1 btn-primary"
                onClick={handleLocationWizardSubmit}
              >
                Save Location
              </Button>
            </div>
            
            <p className="text-xs text-viraly-muted text-center mt-4">
              Your location helps us use relevant hashtags, local references, and appropriate stock photos for your content.
            </p>
          </motion.div>
        </div>
      )}
      
      <div className="max-w-4xl mx-auto py-8 px-4">
        {/* Draft Resume Prompt */}
        {showDraftPrompt && savedDraftData && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-6 rounded-xl bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/30"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                <History className="w-6 h-6 text-purple-400" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-lg font-bold text-purple-400 mb-2">
                  You have unsaved work
                </h3>
                <p className="text-viraly-muted mb-2">
                  Would you like to continue where you left off?
                </p>
                <div className="text-xs text-viraly-muted mb-4 p-2 bg-white/5 rounded-lg">
                  <span className="font-medium text-viraly-light">Draft: </span>
                  {savedDraftData.inputType === "url" && "URL input"}
                  {savedDraftData.inputType === "text" && "Text input"}
                  {savedDraftData.inputType === "image" && "Image upload"}
                  {savedDraftData.inputType === "video" && "Video upload"}
                  {savedDraftData.outputType && ` → ${savedDraftData.outputType === "post" ? "Picture Post" : "Video"}`}
                  {savedDraftData.step > 1 && ` (Step ${savedDraftData.step})`}
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={resumeDraft}
                    className="btn-primary"
                    data-testid="resume-draft-btn"
                  >
                    Continue Where I Left Off
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <Button
                    onClick={discardDraft}
                    variant="outline"
                    className="border-white/20 hover:bg-white/5"
                    data-testid="discard-draft-btn"
                  >
                    Start Fresh
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
        
        {/* No Social Accounts Warning */}
        {connectedPlatforms.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-6 rounded-xl bg-yellow-500/10 border border-yellow-500/30"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-yellow-400" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-lg font-bold text-yellow-400 mb-2">
                  Connect a Social Account to Get Started
                </h3>
                <p className="text-viraly-muted mb-4">
                  To create and publish content, you need to connect at least one social media account 
                  (Facebook, Instagram, TikTok, or Pinterest).
                </p>
                <Button
                  onClick={() => navigate('/settings?tab=social')}
                  className="btn-primary"
                >
                  Connect Account Now
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}
        
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl font-bold mb-2">Create Content</h1>
          <p className="text-viraly-muted">
            {postsRemaining} posts • {totalVideoCredits} video credits remaining
          </p>
        </div>
        
        {/* Auto-Pilot vs Create Your Way Selection */}
        {step === 1 && !inputType && !showAutoPilotSetup && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h2 className="text-center font-heading text-xl font-semibold text-viraly-light mb-4">
              How would you like to create content?
            </h2>
            <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {/* Auto-Pilot Option - Show "Activated" badge if active */}
              {autoPilotStatus?.status === "active" ? (
                <div
                  className="p-6 rounded-xl border border-green-500/30 bg-gradient-to-br from-green-500/10 to-transparent text-left relative"
                  data-testid="auto-pilot-option-active"
                >
                  {/* Activated Badge */}
                  <div className="absolute top-3 right-3 px-2 py-1 rounded-full bg-green-500/20 border border-green-500/30 flex items-center gap-1">
                    <Check className="w-3 h-3 text-green-400" />
                    <span className="text-xs font-medium text-green-400">Activated</span>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center mb-4">
                    <Zap className="w-6 h-6 text-green-400" />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-viraly-light mb-2">
                    Auto-Pilot
                  </h3>
                  <p className="text-sm text-viraly-muted mb-3">
                    Your content is being created automatically!
                    {autoPilotStatus.posts_this_week > 0 && (
                      <span className="block text-green-400 mt-1">
                        {autoPilotStatus.posts_this_week} posts created this week
                      </span>
                    )}
                  </p>
                  <button
                    onClick={() => navigate('/settings?tab=autopilot')}
                    className="text-xs text-green-400 font-medium hover:text-green-300 transition-colors"
                  >
                    Manage Auto-Pilot Settings →
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowAutoPilotSetup(true)}
                  className="p-6 rounded-xl border border-viraly-orange/30 bg-gradient-to-br from-viraly-orange/10 to-transparent hover:border-viraly-orange/50 transition-all text-left group"
                  data-testid="auto-pilot-option"
                >
                  <div className="w-12 h-12 rounded-xl bg-viraly-orange/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Zap className="w-6 h-6 text-viraly-orange" />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-viraly-light mb-2">
                    Auto-Pilot
                  </h3>
                  <p className="text-sm text-viraly-muted mb-3">
                    Let Viraly create content for you automatically based on your brand profile
                  </p>
                  <span className="text-xs text-viraly-orange font-medium">
                    Set it. Forget it. Watch it grow. →
                  </span>
                </button>
              )}
              
              {/* Create Your Way Option */}
              <button
                onClick={() => setShowCreateYourWay(true)}
                className="p-6 rounded-xl border border-white/10 bg-white/5 hover:border-white/30 transition-all text-left group"
                data-testid="create-your-way-option"
              >
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Sparkles className="w-6 h-6 text-viraly-light" />
                </div>
                <h3 className="font-heading text-lg font-bold text-viraly-light mb-2">
                  Create Your Way
                </h3>
                <p className="text-sm text-viraly-muted mb-3">
                  Provide your own inputs (URL, text, image, or voice)
                </p>
                <span className="text-xs text-viraly-muted font-medium">
                  Full control over each post →
                </span>
              </button>
            </div>
          </motion.div>
        )}
        
        {/* Resume from Saved Clip Banner (P2) */}
        {partialClips.length > 0 && step === 1 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 rounded-xl bg-purple-500/10 border border-purple-500/30"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <Video className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="font-medium text-purple-300">Resume Saved Video</p>
                  <p className="text-sm text-viraly-muted">
                    You have {partialClips.length} video{partialClips.length > 1 ? 's' : ''} that can be resumed
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowResumeModal(true)}
                className="border-purple-500/50 text-purple-300 hover:bg-purple-500/20"
                data-testid="resume-video-btn"
              >
                <Scissors className="w-4 h-4 mr-2" />
                View & Resume
              </Button>
            </div>
          </motion.div>
        )}
        
        {/* Step Indicator */}
        {!showAutoPilotSetup && <StepIndicator currentStep={step} totalSteps={3} />}
        
        <AnimatePresence mode="wait">
          {/* Auto-Pilot Setup Flow */}
          {showAutoPilotSetup && (
            <motion.div
              key="auto-pilot"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <AutoPilotSetup 
                onClose={() => setShowAutoPilotSetup(false)}
                onActivated={() => {
                  setShowAutoPilotSetup(false);
                  navigate('/dashboard');
                  toast.success("Auto-Pilot activated! Check your dashboard.");
                }}
              />
            </motion.div>
          )}
          
          {/* STEP 1: Select Input & Enter Content */}
          {step === 1 && !showAutoPilotSetup && (showCreateYourWay || inputType) && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div className="text-center mb-8">
                <h2 className="font-heading text-2xl md:text-3xl font-bold mb-2">What do you have?</h2>
                <p className="text-viraly-muted">Select your content source to get started</p>
                {/* Start Over option */}
                <button
                  onClick={() => {
                    clearDraft();
                    setInputType(null);
                    setInputText("");
                    setInputUrl("");
                    setStep(1);
                    setShowCreateYourWay(false);
                    setShowAutoPilotSetup(false);
                    setOutputType(null);
                    setImageSource(null);
                    setSelectedStockPhoto(null);
                  }}
                  className="text-xs text-viraly-muted hover:text-viraly-orange mt-2 underline underline-offset-2"
                  data-testid="start-over-btn"
                >
                  ← Start Over (Auto-Pilot / Create Your Way)
                </button>
              </div>
              
              {/* Input Type Selection */}
              <div className="space-y-4">
                <InputTypeSelector inputType={inputType} onSelect={setInputType} />
                
                {/* Input Content Area - Appears RIGHT HERE, no scrolling needed */}
                {inputType && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    transition={{ duration: 0.2 }}
                    className="card-base p-5 mt-4"
                  >
                    {inputType === "url" && (
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium mb-2 block">Paste your URL</label>
                          <Input
                            type="url"
                            placeholder="https://yourstore.com/product or any article URL"
                            value={inputUrl}
                            onChange={(e) => setInputUrl(e.target.value)}
                            className="input-base text-base py-3"
                            autoFocus
                            data-testid="input-url"
                          />
                          <p className="text-xs text-viraly-muted mt-2">
                            Works with blogs, product pages, Amazon, Shopify, and social posts
                          </p>
                        </div>
                        
                        {/* Combined Goal + Visual Style + Vision (Phase 4) */}
                        <IntentAndStyleSelector
                          intent={urlIntent}
                          onIntentChange={setUrlIntent}
                          contentStyle={contentStyle}
                          onStyleChange={setContentStyle}
                          context={inputContext}
                          onContextChange={setInputContext}
                          onShowListingDetails={setShowListingDetails}
                          inputType="url"
                        />
                      </div>
                    )}
                    
                    {inputType === "text" && (
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium mb-2 block">Describe your idea</label>
                          <div className="relative">
                            <Textarea
                              placeholder="Example: 'New summer collection launch - 30% off all dresses'"
                              value={inputText}
                              onChange={(e) => setInputText(e.target.value)}
                              rows={3}
                              className="input-base resize-none pr-14 text-base"
                              autoFocus
                              data-testid="input-text"
                            />
                            <div className="absolute right-3 top-3">
                              <VoiceInput 
                                onTranscription={(text) => setInputText(prev => prev ? prev + " " + text : text)}
                                targetLanguage={contentLanguage}
                                className="w-9 h-9"
                              />
                            </div>
                          </div>
                          <p className="text-xs text-viraly-muted mt-2 flex items-center gap-1">
                            <Mic className="w-3 h-3" />
                            Speak in any language - auto-translated
                          </p>
                        </div>
                        
                        {/* Combined Goal + Visual Style + Vision (Phase 4) */}
                        <IntentAndStyleSelector
                          intent={urlIntent}
                          onIntentChange={setUrlIntent}
                          contentStyle={contentStyle}
                          onStyleChange={setContentStyle}
                          context={inputContext}
                          onContextChange={setInputContext}
                          onShowListingDetails={setShowListingDetails}
                          inputType="text"
                        />
                      </div>
                    )}
                    
                    {(inputType === "image" || inputType === "video") && (
                      <div>
                        <label className="text-sm font-medium mb-2 block">
                          Upload your {inputType === "image" ? "image(s)" : "video"}
                        </label>
                        
                        {/* Multiple images supported callout */}
                        {inputType === "image" && (
                          <div className="mb-3 p-3 rounded-lg bg-viraly-orange/10 border border-viraly-orange/20">
                            <div className="flex items-center gap-2">
                              <Layers className="w-4 h-4 text-viraly-orange" />
                              <p className="text-sm text-viraly-light">
                                <span className="font-medium text-viraly-orange">Multiple images supported!</span>
                                {" "}Upload up to 6 images for property listings, portfolios, or before/after posts.
                              </p>
                            </div>
                          </div>
                        )}
                        
                        {inputType === "image" && inputFiles.length === 0 && !inputFile ? (
                          <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-white/20 rounded-xl cursor-pointer hover:border-viraly-orange/50 hover:bg-viraly-orange/5 transition-all">
                            <Upload className="w-8 h-8 text-viraly-muted mb-2" />
                            <p className="text-sm text-viraly-light">Click to upload or drag & drop</p>
                            <p className="text-xs text-viraly-muted mt-1">
                              PNG, JPG, WEBP (up to 6 images, 10MB each)
                            </p>
                            <input
                              type="file"
                              className="hidden"
                              accept="image/*"
                              multiple
                              onChange={(e) => {
                                const files = Array.from(e.target.files || []).slice(0, 6);
                                if (files.length > 0) {
                                  setInputFiles(files);
                                  setInputFile(files[0]); // Keep first file for backward compatibility
                                }
                              }}
                              data-testid="input-image-file"
                            />
                          </label>
                        ) : inputType === "image" && (inputFiles.length > 0 || inputFile) ? (
                          <div className="space-y-3">
                            {/* Scrollable Image Preview Grid */}
                            <div className="relative">
                              <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
                                {(inputFiles.length > 0 ? inputFiles : [inputFile]).map((file, index) => (
                                  <div key={index} className="relative flex-shrink-0 w-28 h-28">
                                    <img 
                                      src={URL.createObjectURL(file)} 
                                      alt={`Preview ${index + 1}`} 
                                      className="w-full h-full object-cover rounded-lg border border-white/10"
                                    />
                                    <button
                                      onClick={() => {
                                        if (inputFiles.length > 0) {
                                          const newFiles = inputFiles.filter((_, i) => i !== index);
                                          setInputFiles(newFiles);
                                          setInputFile(newFiles[0] || null);
                                        } else {
                                          setInputFile(null);
                                        }
                                      }}
                                      className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                    <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-black/60 rounded text-[10px] text-white">
                                      {index + 1}/{inputFiles.length || 1}
                                    </div>
                                  </div>
                                ))}
                                {/* Add more images button - always show when less than 6 */}
                                {(inputFiles.length > 0 || inputFile) && (inputFiles.length || 1) < 6 && (
                                  <label className="flex-shrink-0 w-28 h-28 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-viraly-orange/50 hover:bg-viraly-orange/5 transition-all flex flex-col items-center justify-center">
                                    <Plus className="w-6 h-6 text-viraly-muted" />
                                    <span className="text-xs text-viraly-muted mt-1">Add more</span>
                                    <input
                                      type="file"
                                      className="hidden"
                                      accept="image/*"
                                      multiple
                                      onChange={(e) => {
                                        const newFiles = Array.from(e.target.files || []);
                                        const existingFiles = inputFiles.length > 0 ? inputFiles : (inputFile ? [inputFile] : []);
                                        const combined = [...existingFiles, ...newFiles].slice(0, 6);
                                        setInputFiles(combined);
                                        setInputFile(combined[0]);
                                      }}
                                    />
                                  </label>
                                )}
                              </div>
                              {/* Always show scroll hint when more than 1 image */}
                              {(inputFiles.length || 1) >= 1 && (
                                <p className="text-xs text-viraly-muted mt-1">
                                  {inputFiles.length > 0 ? `${inputFiles.length} images uploaded` : "1 image uploaded"} 
                                  {(inputFiles.length || 1) < 6 && " • Add more using + button"}</p>
                              )}
                            </div>
                            {/* Clear all button */}
                            <button
                              onClick={() => {
                                setInputFiles([]);
                                setInputFile(null);
                                setCollageHeroIndex(0);
                              }}
                              className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1"
                            >
                              <X className="w-3 h-3" /> Clear all images
                            </button>
                            
                            {/* Multi-Image Collage Component - Shows when 2+ images uploaded */}
                            {inputFiles.length >= 2 && (
                              <div className="mt-4">
                                <MultiImageCollage
                                  images={inputFiles.length > 0 ? inputFiles : (inputFile ? [inputFile] : [])}
                                  onImagesChange={(newImages) => {
                                    setInputFiles(newImages);
                                    setInputFile(newImages[0] || null);
                                  }}
                                  heroIndex={collageHeroIndex}
                                  onHeroChange={setCollageHeroIndex}
                                  layout={collageLayout}
                                  onLayoutChange={setCollageLayout}
                                  transition={collageTransition}
                                  onTransitionChange={setCollageTransition}
                                  isVideoOutput={outputType === "video"}
                                  expanded={collageExpanded}
                                  onExpandedChange={setCollageExpanded}
                                />
                              </div>
                            )}
                          </div>
                        ) : inputType === "video" && !inputFile ? (
                          <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-white/20 rounded-xl cursor-pointer hover:border-viraly-orange/50 hover:bg-viraly-orange/5 transition-all">
                            <Upload className="w-8 h-8 text-viraly-muted mb-2" />
                            <p className="text-sm text-viraly-light">Click to upload or drag & drop</p>
                            <p className="text-xs text-viraly-muted mt-1">
                              MP4, MOV up to 100MB
                            </p>
                            <input
                              type="file"
                              className="hidden"
                              accept="video/*"
                              onChange={(e) => e.target.files?.[0] && setInputFile(e.target.files[0])}
                              data-testid="input-video-file"
                            />
                          </label>
                        ) : inputType === "video" && inputFile && (
                          <div className="relative">
                            <video 
                              src={URL.createObjectURL(inputFile)} 
                              className="w-full h-32 object-cover rounded-xl"
                              controls
                            />
                            <button
                              onClick={() => setInputFile(null)}
                              className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500 flex items-center justify-center hover:bg-red-600 transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                        
                        {/* Listing Details REMOVED from Step 1 - now in Step 2 */}
                        
                        {/* Context field for image uploads */}
                        {inputType === "image" && (inputFile || inputFiles.length > 0) && (
                          <InputContextField
                            value={inputContext}
                            onChange={setInputContext}
                            inputType="image"
                          />
                        )}
                      </div>
                    )}
                  </motion.div>
                )}
              </div>
              
              {/* Next Button */}
              <div className="flex justify-end pt-4">
                <Button
                  onClick={handleStep1Continue}
                  disabled={!isStep1Complete()}
                  className="btn-primary px-10 py-3 text-base"
                  data-testid="step1-next"
                >
                  Continue
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}
          
          {/* STEP 2: Select Output Type */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center mb-6">
                <h2 className="font-heading text-xl font-bold">What do you want to create?</h2>
                <p className="text-viraly-muted text-sm">Choose your output format</p>
              </div>
              
              {/* Smart Content Detection Banner - Informational Only */}
              {(smartDetection || isAnalyzingContent) && !smartDetectionDismissed && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mb-4"
                  data-testid="smart-detection-banner"
                >
                  <div className={`p-4 rounded-xl border ${
                    isAnalyzingContent 
                      ? "bg-purple-500/10 border-purple-500/30" 
                      : "bg-gradient-to-r from-viraly-orange/10 to-purple-500/10 border-viraly-orange/30"
                  }`}>
                    {isAnalyzingContent ? (
                      <div className="flex items-center gap-3">
                        <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
                        <p className="text-sm text-purple-300">Analyzing your content...</p>
                      </div>
                    ) : smartDetection && (
                      <div className="space-y-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-viraly-orange" />
                            <p className="text-sm font-medium text-viraly-light">
                              We understood your content as:{" "}
                              <span className="text-viraly-orange capitalize">
                                {smartDetection.detected_category?.replace(/_/g, " ")}
                              </span>
                              {smartDetection.confidence >= 0.8 && (
                                <span className="ml-2 text-xs px-1.5 py-0.5 bg-green-500/20 text-green-400 rounded">
                                  High confidence
                                </span>
                              )}
                            </p>
                          </div>
                          <button
                            onClick={() => setSmartDetectionDismissed(true)}
                            className="text-viraly-muted hover:text-viraly-light p-1"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        
                        {smartDetection.keywords?.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {smartDetection.keywords.slice(0, 5).map((kw, i) => (
                              <span 
                                key={i} 
                                className="text-xs px-2 py-0.5 bg-white/10 text-viraly-muted rounded-full"
                              >
                                {kw}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        {smartDetection.suggested_templates?.length > 0 && (
                          <div className="pt-2 border-t border-white/10">
                            <p className="text-xs text-viraly-muted mb-2">Suggested template categories:</p>
                            <div className="flex flex-wrap gap-2">
                              {smartDetection.suggested_templates.slice(0, 4).map((cat, i) => (
                                <button
                                  key={i}
                                  onClick={() => {
                                    setListingType(cat === "real_estate" ? "property" : cat);
                                    setDesignStyleExpanded(true);
                                  }}
                                  className={`text-xs px-3 py-1 rounded-full transition-all ${
                                    listingType === (cat === "real_estate" ? "property" : cat)
                                      ? "bg-viraly-orange text-white"
                                      : "bg-white/10 text-viraly-light hover:bg-white/20"
                                  }`}
                                >
                                  {cat.replace(/_/g, " ")}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        <p className="text-xs text-viraly-muted italic">
                          This is a suggestion — you can still choose any category below
                        </p>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
              
              {/* Output Type Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {getAvailableOutputs().map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => setOutputType(opt.id)}
                    disabled={opt.locked}
                    className={`p-6 rounded-xl border-2 transition-all text-left relative ${
                      outputType === opt.id 
                        ? "border-viraly-orange bg-viraly-orange/10" 
                        : opt.locked
                          ? "border-white/5 bg-white/5 opacity-60 cursor-not-allowed"
                          : "border-white/10 hover:border-white/20 bg-white/5"
                    }`}
                    data-testid={`output-type-${opt.id}`}
                  >
                    {/* Show Video icon if input is video, otherwise show appropriate icon */}
                    {inputType === "video" || opt.id === "video" ? (
                      <Video className={`w-8 h-8 mb-3 ${outputType === opt.id ? "text-viraly-orange" : "text-viraly-muted"}`} />
                    ) : (
                      <ImageIcon className={`w-8 h-8 mb-3 ${outputType === opt.id ? "text-viraly-orange" : "text-viraly-muted"}`} />
                    )}
                    <p className="font-heading text-lg font-bold">{opt.name}</p>
                    <p className="text-sm text-viraly-muted">{opt.desc}</p>
                    <div className="mt-3 pt-3 border-t border-white/10">
                      <p className="text-xs">
                        Uses: <span className={`font-medium ${opt.isVideo ? "text-purple-400" : "text-blue-400"}`}>
                          {opt.credits} {opt.isVideo ? "video" : "post"} credit{opt.credits > 1 ? "s" : ""}
                        </span>
                      </p>
                    </div>
                    
                    {opt.locked && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-xl">
                        <div className="text-center p-4">
                          <Crown className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
                          <p className="text-sm font-medium">Upgrade to Create Videos</p>
                          <p className="text-xs text-viraly-muted">
                            {isTrialUser ? "Not available in free trial" : "Requires video credits"}
                          </p>
                        </div>
                      </div>
                    )}
                  </button>
                ))}
              </div>
              
              {/* Content Style Summary - Already selected in Step 1 */}
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-purple-400" />
                    <span className="text-sm text-viraly-light">Visual Style:</span>
                    <span className="text-sm font-medium text-purple-400 capitalize">{contentStyle}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="text-xs text-viraly-muted hover:text-viraly-orange transition-colors"
                  >
                    Change
                  </button>
                </div>
              </div>
              
              {/* Image Source Selection - For Picture Posts */}
              {outputType === "post" && imageSourceRecommendation && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="card-base p-5"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <Camera className="w-4 h-4 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-viraly-light">Image Type</h3>
                      <p className="text-xs text-viraly-muted">Choose between real photos or AI-generated images</p>
                    </div>
                  </div>
                  
                  {/* Recommendation Badge */}
                  {imageSourceRecommendation.confidence !== "low" && (
                    <div className="mb-3 p-2 rounded-lg bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-500/20">
                      <p className="text-xs text-green-400">
                        <span className="font-semibold">Recommended:</span>{" "}
                        {imageSourceRecommendation.recommended_source === "stock" 
                          ? "Real Photos — Best for landmarks, food, people, and real-world content"
                          : imageSourceRecommendation.recommended_source === "ai"
                          ? "AI Generated — Best for creative, conceptual, and branded content"
                          : "Either works well for this content"}
                      </p>
                    </div>
                  )}
                  
                  {/* Image Source Toggle */}
                  <div className="grid grid-cols-2 gap-3">
                    {/* Stock Photos Option */}
                    <button
                      type="button"
                      onClick={() => {
                        setImageSource("stock");
                        // Trigger stock photo search
                        if (imageSourceRecommendation.suggested_search_query) {
                          searchStockPhotos(imageSourceRecommendation.suggested_search_query);
                        }
                      }}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        imageSource === "stock"
                          ? "border-blue-500 bg-blue-500/10"
                          : "border-white/10 hover:border-white/20 bg-white/5"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <ImageIcon className="w-5 h-5 text-blue-400" />
                        <span className="font-semibold text-white">Real Photos</span>
                        {imageSource === "stock" && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-blue-500 text-white rounded">Selected</span>
                        )}
                      </div>
                      <p className="text-xs text-viraly-muted">
                        Professional stock photos from Unsplash
                      </p>
                      <p className="text-[10px] text-blue-400 mt-1">
                        Best for: Landmarks, food, fitness, travel, nature
                      </p>
                    </button>
                    
                    {/* AI Generated Option */}
                    <button
                      type="button"
                      onClick={() => {
                        setImageSource("ai");
                        setStockPhotoResults([]);
                        setSelectedStockPhoto(null);
                      }}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        imageSource === "ai"
                          ? "border-purple-500 bg-purple-500/10"
                          : "border-white/10 hover:border-white/20 bg-white/5"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-5 h-5 text-purple-400" />
                        <span className="font-semibold text-white">AI Generated</span>
                        {imageSource === "ai" && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-purple-500 text-white rounded">Selected</span>
                        )}
                      </div>
                      <p className="text-xs text-viraly-muted">
                        Custom AI-created images matching your brand
                      </p>
                      <p className="text-[10px] text-purple-400 mt-1">
                        Best for: Conceptual, branding, creative, abstract
                      </p>
                    </button>
                  </div>
                  
                  {/* Smart Image Mode Toggle - Only for AI Generated */}
                  {imageSource === "ai" && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-4 p-4 rounded-xl bg-purple-500/5 border border-purple-500/20"
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <UserCircle className="w-4 h-4 text-purple-400" />
                        <span className="text-sm font-medium text-viraly-light">Image Style</span>
                        <span className="text-[10px] px-1.5 py-0.5 bg-purple-500/20 text-purple-300 rounded">Smart AI</span>
                      </div>
                      <p className="text-xs text-viraly-muted mb-3">
                        Control whether AI generates images with people or abstract visuals
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        <button
                          type="button"
                          onClick={() => setSmartImageMode("auto")}
                          className={`p-3 rounded-lg border text-center transition-all ${
                            smartImageMode === "auto"
                              ? "border-purple-500 bg-purple-500/20"
                              : "border-white/10 hover:border-white/20 bg-white/5"
                          }`}
                        >
                          <Sparkles className="w-4 h-4 mx-auto mb-1 text-purple-400" />
                          <span className="text-xs font-medium text-white block">Auto-Detect</span>
                          <span className="text-[10px] text-viraly-muted block mt-0.5">AI decides</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setSmartImageMode("abstract")}
                          className={`p-3 rounded-lg border text-center transition-all ${
                            smartImageMode === "abstract"
                              ? "border-purple-500 bg-purple-500/20"
                              : "border-white/10 hover:border-white/20 bg-white/5"
                          }`}
                        >
                          <Layout className="w-4 h-4 mx-auto mb-1 text-purple-400" />
                          <span className="text-xs font-medium text-white block">Abstract</span>
                          <span className="text-[10px] text-viraly-muted block mt-0.5">No faces</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setSmartImageMode("lifestyle")}
                          className={`p-3 rounded-lg border text-center transition-all ${
                            smartImageMode === "lifestyle"
                              ? "border-purple-500 bg-purple-500/20"
                              : "border-white/10 hover:border-white/20 bg-white/5"
                          }`}
                        >
                          <User className="w-4 h-4 mx-auto mb-1 text-purple-400" />
                          <span className="text-xs font-medium text-white block">Lifestyle</span>
                          <span className="text-[10px] text-viraly-muted block mt-0.5">With people</span>
                        </button>
                      </div>
                      {smartImageMode === "auto" && (
                        <p className="text-[10px] text-purple-300 mt-2">
                          AI will detect personal service posts (tutoring, coaching) and use abstract imagery
                        </p>
                      )}
                      {smartImageMode === "abstract" && (
                        <p className="text-[10px] text-purple-300 mt-2">
                          Best for: Personal brands, services, consultants — avoids generic AI faces
                        </p>
                      )}
                      {smartImageMode === "lifestyle" && (
                        <p className="text-[10px] text-purple-300 mt-2">
                          Best for: Events, celebrations, team photos — includes natural people
                        </p>
                      )}
                    </motion.div>
                  )}
                  
                  {/* Stock Photo Search Results */}
                  {imageSource === "stock" && (
                    <div className="mt-4">
                      {stockPhotoSearching ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
                          <span className="ml-2 text-sm text-viraly-muted">AI is selecting the best photos...</span>
                        </div>
                      ) : stockPhotoResults.length > 0 ? (
                        <div>
                          <p className="text-xs text-viraly-muted mb-2">AI suggested photos (optional - AI will auto-select if you skip):</p>
                          <div className="grid grid-cols-4 gap-2">
                            {stockPhotoResults.map((photo, idx) => (
                              <button
                                key={photo.id || idx}
                                type="button"
                                onClick={() => setSelectedStockPhoto(photo)}
                                className={`relative aspect-video rounded-lg overflow-hidden border-2 transition-all ${
                                  selectedStockPhoto?.id === photo.id
                                    ? "border-blue-500 ring-2 ring-blue-500/50"
                                    : "border-transparent hover:border-white/30"
                                }`}
                              >
                                <img
                                  src={photo.url_thumb || photo.url_regular}
                                  alt={photo.description || "Stock photo"}
                                  className="w-full h-full object-cover"
                                />
                                {selectedStockPhoto?.id === photo.id && (
                                  <div className="absolute inset-0 bg-blue-500/20 flex items-center justify-center">
                                    <Check className="w-6 h-6 text-white" />
                                  </div>
                                )}
                              </button>
                            ))}
                          </div>
                          {selectedStockPhoto && (
                            <p className="text-xs text-viraly-muted mt-2">
                              Photo by {selectedStockPhoto.photographer} on Unsplash
                            </p>
                          )}
                          {/* Custom Search */}
                          <div className="mt-3 flex gap-2">
                            <input
                              type="text"
                              placeholder="Search different photos..."
                              className="input-base flex-1 text-sm"
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  searchStockPhotos(e.target.value);
                                }
                              }}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const input = document.querySelector('input[placeholder="Search different photos..."]');
                                if (input?.value) searchStockPhotos(input.value);
                              }}
                            >
                              Search
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-4 text-viraly-muted">
                          <Check className="w-8 h-8 mx-auto mb-2 text-blue-400" />
                          <p className="text-sm font-medium text-viraly-light">Real Photos Selected</p>
                          <p className="text-xs">AI will automatically select the best photos when generating</p>
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              )}
              
              {/* Hint to select Image Type before Design Style appears */}
              {outputType === "post" && !imageSource && imageSourceRecommendation && (
                <div className="p-4 rounded-xl bg-white/5 border border-white/10 border-dashed">
                  <div className="flex items-center gap-3 text-viraly-muted">
                    <Layout className="w-5 h-5" />
                    <div>
                      <p className="text-sm font-medium text-viraly-light">Design Style</p>
                      <p className="text-xs">Select an Image Type above to unlock design options</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Template Selection - For Image Posts - Shows only after Image Type is selected (progressive disclosure) */}
              {outputType === "post" && imageSource && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="card-base p-5"
                >
                  {/* Header - Always visible, click to expand */}
                  <button
                    type="button"
                    onClick={() => setDesignStyleExpanded(!designStyleExpanded)}
                    className="w-full flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <Layout className="w-5 h-5 text-viraly-orange" />
                      <div className="text-left">
                        <h3 className="font-medium text-viraly-light flex items-center gap-2">
                          Design Style
                          <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded">FREE</span>
                          {/* Show "Optional" tag for text/voice inputs */}
                          {(inputType === "text" || inputType === "url") && (
                            <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded">Optional</span>
                          )}
                        </h3>
                        <p className="text-xs text-viraly-muted mt-0.5">
                          {selectedTemplate 
                            ? `Using: ${selectedTemplate.name}` 
                            : "AI Generated"}
                          {(inputType === "text" || inputType === "url") && !designStyleExpanded && (
                            <span className="ml-2 text-blue-400">— AI will handle design</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {/* Quick badges when collapsed */}
                      {!designStyleExpanded && (
                        <div className="flex items-center gap-2">
                          {Object.values(listingDetails).some(v => v) && (
                            <span className="text-xs text-green-400 px-2 py-0.5 bg-green-500/10 rounded">✓ Details</span>
                          )}
                          {selectedTemplate && (
                            <span className="text-xs text-purple-400 px-2 py-0.5 bg-purple-500/10 rounded">Template</span>
                          )}
                        </div>
                      )}
                      <ChevronDown className={`w-5 h-5 text-viraly-muted transition-transform ${designStyleExpanded ? 'rotate-180' : ''}`} />
                    </div>
                  </button>
                  
                  {/* Collapsed view - Quick actions */}
                  {!designStyleExpanded && (
                    <div className="mt-3 pt-3 border-t border-white/10">
                      {/* Clear Choice: AI Generated OR Browse Templates */}
                      <div className="mb-3 p-3 rounded-lg bg-gradient-to-r from-viraly-orange/10 to-purple-500/10 border border-viraly-orange/20">
                        <p className="text-sm font-medium text-viraly-light mb-2">Choose your design approach:</p>
                        <div className="grid grid-cols-2 gap-3">
                          {/* AI Generated Option */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTemplate(null);
                              setDesignStyleExpanded(true);
                            }}
                            className={`p-3 rounded-lg border-2 text-left transition-all ${
                              !selectedTemplate 
                                ? "border-viraly-orange bg-viraly-orange/10" 
                                : "border-white/10 hover:border-white/20 bg-white/5"
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <Sparkles className="w-4 h-4 text-viraly-orange" />
                              <span className="font-medium text-sm">AI Generated</span>
                              {!selectedTemplate && <span className="text-[10px] px-1.5 py-0.5 bg-viraly-orange text-white rounded">Selected</span>}
                            </div>
                            <p className="text-[10px] text-viraly-muted">Let AI create a unique design for you</p>
                          </button>
                          
                          {/* OR Divider */}
                          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-viraly-dark px-2 text-xs text-viraly-muted hidden">OR</div>
                          
                          {/* Browse Templates Option */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowTemplateSelector(true);
                            }}
                            className={`p-3 rounded-lg border-2 text-left transition-all ${
                              selectedTemplate 
                                ? "border-purple-500 bg-purple-500/10" 
                                : "border-white/10 hover:border-white/20 bg-white/5"
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <Layout className="w-4 h-4 text-purple-400" />
                              <span className="font-medium text-sm">Browse Templates</span>
                              {selectedTemplate && <span className="text-[10px] px-1.5 py-0.5 bg-purple-500 text-white rounded">Selected</span>}
                            </div>
                            <p className="text-[10px] text-viraly-muted">Choose from 600 pre-made designs</p>
                          </button>
                        </div>
                        
                        {/* Separator with OR */}
                        <div className="flex items-center gap-2 my-2">
                          <div className="flex-1 h-px bg-white/10"></div>
                          <span className="text-xs text-viraly-muted font-medium">OR</span>
                          <div className="flex-1 h-px bg-white/10"></div>
                        </div>
                        
                        {/* Tip for adding details */}
                        <p className="text-xs text-blue-400">
                          <span className="font-semibold">Tip:</span> Add details like price, address, or features that will appear ON your post!
                          <button 
                            onClick={(e) => { e.stopPropagation(); setDesignStyleExpanded(true); }}
                            className="ml-2 text-viraly-orange hover:underline"
                          >
                            + Add details →
                          </button>
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Expanded content */}
                  <AnimatePresence>
                    {designStyleExpanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                      >
                        {/* Template browse button */}
                        <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
                          <p className="text-xs text-viraly-muted">
                            {selectedTemplate 
                              ? "Template selected - customize below" 
                              : "Using AI to generate unique design"}
                          </p>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setShowTemplateSelector(true)}
                            className="flex items-center gap-2"
                          >
                            {selectedTemplate ? "Change Template" : "Browse 500 Templates"}
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                  
                        {/* LISTING DETAILS & FIELD POSITIONING - Works for BOTH AI Generated and Templates */}
                        <div className="mt-4 pt-4 border-t border-white/10">
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-medium text-viraly-light flex items-center gap-2">
                              <FileText className="w-4 h-4 text-viraly-orange" />
                              {selectedTemplate ? "Customize Your Template" : "Add Your Details"}
                            </p>
                            {Object.values(listingDetails).some(v => v) && (
                              <span className="text-xs text-green-400">✓ Details added</span>
                            )}
                          </div>
                          
                          {/* Field Position Mapper - Same UI for both AI and Templates */}
                          <FieldPositionMapper
                            category={listingType}
                            fieldValues={listingDetails}
                            setFieldValues={setListingDetails}
                            fieldPositions={fieldPositions}
                            setFieldPositions={setFieldPositions}
                            template={selectedTemplate}
                            textStyle={templateTextStyle}
                            setTextStyle={setTemplateTextStyle}
                            isAIGenerated={!selectedTemplate}
                          />
                          
                          {/* Category Selector */}
                          <div className="mt-4 pt-4 border-t border-white/10">
                            <p className="text-xs text-viraly-muted mb-2">What type of content is this?</p>
                            <div className="flex flex-wrap gap-2">
                              {[
                                { id: "property", label: "Property" },
                                { id: "product", label: "Product" },
                                { id: "restaurant", label: "Restaurant" },
                                { id: "service", label: "Service" },
                                { id: "event", label: "Event" },
                                { id: "spiritual", label: "Spiritual" },
                                { id: "events_celebrations", label: "Celebrations" },
                                { id: "motivational", label: "Motivational" },
                                { id: "nature", label: "Nature" },
                                { id: "job", label: "Job" },
                                { id: "automotive", label: "Automotive" },
                                { id: "fitness", label: "Fitness" },
                                { id: "travel", label: "Travel" },
                                { id: "education", label: "Education" },
                                { id: "pet", label: "Pet" },
                                { id: "rental", label: "Rental" },
                              ].map(cat => (
                                <button
                                  key={cat.id}
                                  onClick={() => {
                                    // Toggle behavior - click again to deselect
                                    if (listingType === cat.id) {
                                      setListingType(null);
                                      setListingDetails({});
                                      setFieldPositions({});
                                    } else {
                                      setListingType(cat.id);
                                      setListingDetails({});
                                      setFieldPositions({});
                                    }
                                  }}
                                  className={`px-3 py-1 rounded-full text-xs transition-all ${
                                    listingType === cat.id
                                      ? "bg-viraly-orange text-white"
                                      : "bg-white/10 text-viraly-muted hover:bg-white/20"
                                  }`}
                                >
                                  {cat.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          
                          {/* Template Selected - Show option to use AI instead */}
                          {selectedTemplate && (
                            <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div 
                                  className="w-10 h-10 rounded-lg"
                                  style={{
                                    background: selectedTemplate.background_style?.type === "gradient"
                                      ? `linear-gradient(135deg, ${selectedTemplate.background_style.colors?.join(", ") || "#1a1a2e, #16213e"})`
                                      : selectedTemplate.background_style?.color || "#1a1a2e"
                                  }}
                                />
                                <div>
                                  <p className="text-sm font-medium">{selectedTemplate.name}</p>
                                  <p className="text-xs text-viraly-muted capitalize">{selectedTemplate.category}</p>
                                </div>
                              </div>
                              <button
                                onClick={() => setSelectedTemplate(null)}
                                className="text-xs text-viraly-muted hover:text-viraly-light"
                              >
                                Use AI Instead
                              </button>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              )}
              
              {/* VIDEO SETTINGS - Duration & Quality Selector */}
              {outputType === "video" && hasVideoCredits && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="card-base p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <label className="text-sm font-medium text-viraly-light">Video Settings</label>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-viraly-muted">Your credits:</span>
                      <span className="text-sm font-bold text-purple-400">{totalVideoCredits}</span>
                    </div>
                  </div>
                  
                  {/* Plan Info Badge */}
                  <div className={`mb-4 p-3 rounded-lg ${isProUser ? 'bg-purple-500/10 border border-purple-500/30' : 'bg-blue-500/10 border border-blue-500/30'}`}>
                    <div className="flex items-center gap-2">
                      {isProUser ? (
                        <>
                          <Crown className="w-4 h-4 text-purple-400" />
                          <span className="text-sm font-medium text-purple-400">Pro Plan</span>
                          <span className="text-xs text-viraly-muted">— 1080p HD videos (20s)</span>
                        </>
                      ) : (
                        <>
                          <Zap className="w-4 h-4 text-blue-400" />
                          <span className="text-sm font-medium text-blue-400">Growth Plan</span>
                          <span className="text-xs text-viraly-muted">— 720p videos (10s/20s)</span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Duration Selection */}
                  <div className="mb-4">
                    <p className="text-xs text-viraly-muted mb-2">Duration</p>
                    <div className="grid grid-cols-3 gap-2">
                      {isGrowthUser && (
                        <>
                          <button
                            onClick={() => { setVideoDuration(10); setVideoQuality("720p"); }}
                            className={`p-3 rounded-lg border-2 transition-all text-center ${
                              videoDuration === 10
                                ? "border-viraly-orange bg-viraly-orange/10"
                                : "border-white/10 hover:border-white/20"
                            }`}
                            data-testid="video-duration-10s"
                          >
                            <p className="font-bold text-lg">10s</p>
                            <p className="text-xs text-viraly-muted">Quick Clip</p>
                            <p className="text-xs text-purple-400 mt-1">1 credit</p>
                          </button>
                          <button
                            onClick={() => { setVideoDuration(20); setVideoQuality("720p"); }}
                            className={`p-3 rounded-lg border-2 transition-all text-center ${
                              videoDuration === 20
                                ? "border-viraly-orange bg-viraly-orange/10"
                                : "border-white/10 hover:border-white/20"
                            }`}
                            data-testid="video-duration-20s"
                          >
                            <p className="font-bold text-lg">20s</p>
                            <p className="text-xs text-viraly-muted">Standard</p>
                            <p className="text-xs text-purple-400 mt-1">2 credits</p>
                          </button>
                          <button
                            disabled
                            className="p-3 rounded-lg border-2 border-white/5 bg-white/5 opacity-50 cursor-not-allowed text-center relative"
                            title="Extended videos available via Pay As You Go"
                          >
                            <p className="font-bold text-lg">30s+</p>
                            <p className="text-xs text-viraly-muted">Extended</p>
                            <div className="absolute -top-2 -right-2 bg-green-500 text-white text-[10px] px-2 py-0.5 rounded-full">
                              PAYG
                            </div>
                          </button>
                        </>
                      )}
                      {isProUser && (
                        <>
                          <button
                            onClick={() => { setVideoDuration(20); setVideoQuality("720p"); }}
                            className={`p-3 rounded-lg border-2 transition-all text-center ${
                              videoDuration === 20 && videoQuality === "720p"
                                ? "border-viraly-orange bg-viraly-orange/10"
                                : "border-white/10 hover:border-white/20"
                            }`}
                            data-testid="video-duration-20s-720p-pro"
                          >
                            <p className="font-bold text-lg">20s</p>
                            <p className="text-xs text-blue-400">720p</p>
                            <p className="text-xs text-purple-400 mt-1">2 credits</p>
                          </button>
                          <button
                            onClick={() => { setVideoDuration(20); setVideoQuality("1080p"); }}
                            className={`p-3 rounded-lg border-2 transition-all text-center ${
                              videoDuration === 20 && videoQuality === "1080p"
                                ? "border-viraly-orange bg-viraly-orange/10"
                                : "border-white/10 hover:border-white/20"
                            }`}
                            data-testid="video-duration-20s-hd"
                          >
                            <p className="font-bold text-lg">20s</p>
                            <p className="text-xs text-purple-400">1080p HD</p>
                            <p className="text-xs text-purple-400 mt-1">4 credits</p>
                          </button>
                        </>
                      )}
                    </div>
                    
                    {/* Longer Videos? Upsell */}
                    <div className="mt-3 p-3 rounded-lg bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-500/20">
                      <button
                        onClick={() => setShowExtendedVideoOptions(!showExtendedVideoOptions)}
                        className="w-full flex items-center justify-between"
                      >
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-green-400" />
                          <span className="text-sm font-medium text-green-400">Need longer videos? (30s - 60s)</span>
                        </div>
                        <ChevronDown className={`w-4 h-4 text-green-400 transition-transform ${showExtendedVideoOptions ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {showExtendedVideoOptions && (
                        <div className="mt-3 space-y-3">
                          {/* Duration Selection */}
                          <div>
                            <p className="text-xs text-viraly-muted mb-2">Select Duration:</p>
                            <div className="grid grid-cols-4 gap-2">
                              {[30, 40, 50, 60].map(dur => (
                                <button
                                  key={dur}
                                  onClick={() => setExtendedVideoDuration(extendedVideoDuration === dur ? null : dur)}
                                  className={`p-2 rounded-lg border-2 text-center transition-all ${
                                    extendedVideoDuration === dur
                                      ? "border-green-500 bg-green-500/10"
                                      : "border-white/10 hover:border-white/20"
                                  }`}
                                >
                                  <p className="font-bold text-sm">{dur}s</p>
                                </button>
                              ))}
                            </div>
                          </div>
                          
                          {/* Quality Selection (Pro only for 1080p) */}
                          {extendedVideoDuration && (
                            <div>
                              <p className="text-xs text-viraly-muted mb-2">Select Quality:</p>
                              <div className="grid grid-cols-2 gap-2">
                                <button
                                  onClick={() => setExtendedVideoQuality("720p")}
                                  className={`p-2 rounded-lg border-2 text-center transition-all ${
                                    extendedVideoQuality === "720p"
                                      ? "border-blue-500 bg-blue-500/10"
                                      : "border-white/10 hover:border-white/20"
                                  }`}
                                >
                                  <p className="font-bold text-sm text-blue-400">720p</p>
                                  <p className="text-xs text-viraly-muted">
                                    ${extendedVideoDuration === 30 ? 6 : extendedVideoDuration === 40 ? 8 : extendedVideoDuration === 50 ? 10 : 12}
                                  </p>
                                </button>
                                {isProUser && (
                                  <button
                                    onClick={() => setExtendedVideoQuality("1080p")}
                                    className={`p-2 rounded-lg border-2 text-center transition-all ${
                                      extendedVideoQuality === "1080p"
                                        ? "border-purple-500 bg-purple-500/10"
                                        : "border-white/10 hover:border-white/20"
                                    }`}
                                  >
                                    <p className="font-bold text-sm text-purple-400">1080p HD</p>
                                    <p className="text-xs text-viraly-muted">
                                      ${extendedVideoDuration === 30 ? 25 : extendedVideoDuration === 40 ? 32 : extendedVideoDuration === 50 ? 40 : 48}
                                    </p>
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Credit Calculation */}
                          {extendedVideoDuration && (
                            <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm text-viraly-muted">Your credits:</span>
                                <span className="text-sm font-bold">{totalVideoCredits} video credits</span>
                              </div>
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm text-viraly-muted">Extended {extendedVideoDuration}s {extendedVideoQuality}:</span>
                                <span className="text-sm font-bold text-green-400">
                                  ${extendedVideoQuality === "720p" 
                                    ? (extendedVideoDuration === 30 ? 6 : extendedVideoDuration === 40 ? 8 : extendedVideoDuration === 50 ? 10 : 12)
                                    : (extendedVideoDuration === 30 ? 25 : extendedVideoDuration === 40 ? 32 : extendedVideoDuration === 50 ? 40 : 48)
                                  } PAYG
                                </span>
                              </div>
                              <button
                                onClick={() => navigate(`/pricing?tab=payg&duration=${extendedVideoDuration}&quality=${extendedVideoQuality}`)}
                                className="w-full mt-2 py-2 bg-green-500 hover:bg-green-600 rounded-lg text-sm font-medium transition-colors"
                              >
                                Purchase Extended Video Pack →
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Quality Display */}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-viraly-muted">Quality:</span>
                      <span className={`text-sm font-bold ${videoQuality === "1080p" ? "text-purple-400" : "text-blue-400"}`}>
                        {videoQuality === "1080p" ? "1080p HD" : "720p"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-viraly-muted">Cost:</span>
                      <span className="text-sm font-bold text-viraly-orange">
                        {getVideoCreditsNeededForSelection()} credit{getVideoCreditsNeededForSelection() > 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                  
                  {/* Credits Warning */}
                  {!hasEnoughVideoCredits && totalVideoCreditsNeeded > 0 && (
                    <div className="mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                        <p className="text-sm text-red-400">
                          Need {totalVideoCreditsNeeded} credit{totalVideoCreditsNeeded > 1 ? 's' : ''}, you have {totalVideoCredits}
                        </p>
                      </div>
                      <button 
                        onClick={() => navigate('/pricing?tab=payg')}
                        className="mt-2 text-xs text-viraly-orange hover:underline"
                      >
                        Buy more credits →
                      </button>
                    </div>
                  )}
                </motion.div>
              )}
              
              {/* VIDEO OVERLAYS & DETAILS - For Video Output */}
              {outputType === "video" && hasVideoCredits && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="card-base p-5"
                >
                  {/* Video Overlays Toggle Header */}
                  <label className="flex items-center justify-between cursor-pointer">
                    <div className="flex items-center gap-3">
                      <input 
                        type="checkbox" 
                        checked={videoOverlayStyle !== "none"}
                        onChange={(e) => setVideoOverlayStyle(e.target.checked ? "static" : "none")}
                        className="w-5 h-5 rounded border-white/20 bg-white/5 text-viraly-orange focus:ring-viraly-orange"
                      />
                      <div>
                        <h3 className="font-medium text-viraly-light flex items-center gap-2">
                          <Layout className="w-4 h-4 text-viraly-orange" />
                          Video Overlays
                          <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded">Optional</span>
                        </h3>
                        <p className="text-xs text-viraly-muted mt-1">
                          Add text details (price, address) on your video
                        </p>
                      </div>
                    </div>
                  </label>
                  
                  {/* Expandable Content - Only show when enabled */}
                  {videoOverlayStyle !== "none" && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-4"
                    >
                      {/* Choose Overlay Style: Static or Animated */}
                      <div className="mb-4 p-3 rounded-lg bg-white/5 border border-white/10">
                        <p className="text-xs text-viraly-muted mb-2">How should details appear?</p>
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            onClick={() => setVideoOverlayStyle && setVideoOverlayStyle("static")}
                            className={`p-2 rounded-lg text-left transition-all ${
                              videoOverlayStyle === "static"
                                ? "bg-purple-500/20 border-purple-500 border"
                                : "bg-white/5 border-white/10 border hover:bg-white/10"
                            }`}
                          >
                            <p className="text-xs font-medium">Static Text</p>
                            <p className="text-[10px] text-viraly-muted">Text appears fixed on screen</p>
                          </button>
                          <button
                            onClick={() => setVideoOverlayStyle && setVideoOverlayStyle("animated")}
                            className={`p-2 rounded-lg text-left transition-all ${
                              videoOverlayStyle === "animated"
                                ? "bg-purple-500/20 border-purple-500 border"
                                : "bg-white/5 border-white/10 border hover:bg-white/10"
                            }`}
                          >
                            <p className="text-xs font-medium">Animated Overlays</p>
                            <p className="text-[10px] text-viraly-muted">Fly in, fade, slide effects</p>
                          </button>
                        </div>
                        
                        {/* Animation Options - Only show if animated selected */}
                        {videoOverlayStyle === "animated" && (
                          <div className="mt-3 pt-3 border-t border-white/10">
                            <p className="text-xs text-viraly-muted mb-2">Choose animation style:</p>
                            <div className="flex flex-wrap gap-1">
                              {[
                                { id: "fade", label: "Fade In" },
                                { id: "fly_in_left", label: "Fly In (Left)" },
                                { id: "fly_in_right", label: "Fly In (Right)" },
                                { id: "fly_in_top", label: "Fly In (Top)" },
                                { id: "fly_in_bottom", label: "Fly In (Bottom)" },
                                { id: "slide", label: "Slide" },
                                { id: "zoom", label: "Zoom" },
                                { id: "wipe", label: "Wipe" }
                              ].map(anim => (
                                <button
                                  key={anim.id}
                                  onClick={() => setVideoOverlayAnimation && setVideoOverlayAnimation(anim.id)}
                                  className={`px-2 py-1 rounded text-[10px] transition-all ${
                                    videoOverlayAnimation === anim.id
                                      ? "bg-viraly-orange text-white"
                                      : "bg-white/10 text-viraly-muted hover:bg-white/20"
                                  }`}
                                >
                                  {anim.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* VIDEO DETAILS & FIELD POSITIONING */}
                      <div className="pt-4 border-t border-white/10">
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-sm font-medium text-viraly-light flex items-center gap-2">
                            <FileText className="w-4 h-4 text-viraly-orange" />
                            Add Video Details
                          </p>
                          {Object.values(listingDetails).some(v => v) ? (
                            <span className="text-xs text-green-400">✓ Details added</span>
                          ) : (
                            <span className="text-xs text-viraly-muted">No details yet</span>
                          )}
                        </div>
                        
                        {/* Field Position Mapper */}
                        <FieldPositionMapper
                          category={listingType}
                          fieldValues={listingDetails}
                          setFieldValues={setListingDetails}
                          fieldPositions={fieldPositions}
                          setFieldPositions={setFieldPositions}
                          template={null}
                          textStyle={templateTextStyle}
                          setTextStyle={setTemplateTextStyle}
                          isAIGenerated={true}
                        />
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              )}
              
              {/* FEATURE YOURSELF - Video Headshot Overlay */}
              {outputType === "video" && hasVideoCredits && user?.headshot_url && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="card-base p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <UserCircle className="w-5 h-5 text-viraly-orange" />
                      <label className="text-sm font-medium text-viraly-light">Feature Yourself</label>
                    </div>
                    <span className="text-xs px-2 py-1 rounded bg-green-500/20 text-green-400">FREE</span>
                  </div>
                  
                  <p className="text-xs text-viraly-muted mb-4">
                    Show your face in the video as the presenter. Your headshot appears in the corner while AI-generated content plays.
                  </p>
                  
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => setFeatureYourself("none")}
                      className={`p-3 rounded-lg border-2 transition-all text-center ${
                        featureYourself === "none"
                          ? "border-viraly-orange bg-viraly-orange/10"
                          : "border-white/10 hover:border-white/20"
                      }`}
                      data-testid="feature-yourself-none"
                    >
                      <Video className="w-5 h-5 mx-auto mb-1 text-viraly-muted" />
                      <p className="text-xs font-medium">No</p>
                      <p className="text-[10px] text-viraly-muted">Content only</p>
                    </button>
                    
                    <button
                      onClick={() => setFeatureYourself("subtle")}
                      className={`p-3 rounded-lg border-2 transition-all text-center ${
                        featureYourself === "subtle"
                          ? "border-viraly-orange bg-viraly-orange/10"
                          : "border-white/10 hover:border-white/20"
                      }`}
                      data-testid="feature-yourself-subtle"
                    >
                      <div className="relative w-8 h-8 mx-auto mb-1">
                        <Video className="w-8 h-8 text-viraly-muted" />
                        <div className="absolute -bottom-1 -left-1 w-4 h-4 rounded-full bg-viraly-orange/50 border border-white/50" />
                      </div>
                      <p className="text-xs font-medium">Subtle</p>
                      <p className="text-[10px] text-viraly-muted">Corner headshot</p>
                    </button>
                    
                    <button
                      onClick={() => setFeatureYourself("prominent")}
                      className={`p-3 rounded-lg border-2 transition-all text-center ${
                        featureYourself === "prominent"
                          ? "border-viraly-orange bg-viraly-orange/10"
                          : "border-white/10 hover:border-white/20"
                      }`}
                      data-testid="feature-yourself-prominent"
                    >
                      <div className="relative w-8 h-8 mx-auto mb-1">
                        <Video className="w-8 h-8 text-viraly-muted" />
                        <div className="absolute -bottom-1 -left-1 w-5 h-5 rounded-full bg-viraly-orange border-2 border-white flex items-center justify-center">
                          <User className="w-3 h-3 text-white" />
                        </div>
                      </div>
                      <p className="text-xs font-medium">Prominent</p>
                      <p className="text-[10px] text-viraly-muted">Headshot + name</p>
                    </button>
                  </div>
                  
                  {featureYourself !== "none" && (
                    <div className="mt-3 flex items-center gap-2 p-2 rounded-lg bg-white/5">
                      {user?.headshot_url && (
                        <img 
                          src={user.headshot_url.startsWith('/') ? `${import.meta.env.VITE_BACKEND_URL || ''}${user.headshot_url}` : user.headshot_url}
                          alt="Your headshot"
                          className="w-8 h-8 rounded-full object-cover border border-white/20"
                        />
                      )}
                      <div className="flex-1">
                        <p className="text-xs text-viraly-light">{user?.name || 'You'}</p>
                        <p className="text-[10px] text-viraly-muted">Will appear in {featureYourself} style</p>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
              
              {/* Show prompt to upload headshot if not available */}
              {outputType === "video" && !isTrialUser && !isStarterUser && !user?.headshot_url && (
                <div className="card-base p-4 border border-dashed border-white/20">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                      <UserCircle className="w-5 h-5 text-viraly-muted" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-viraly-light">Want to appear in your videos?</p>
                      <p className="text-xs text-viraly-muted">Upload your headshot in Settings to feature yourself</p>
                    </div>
                    <button 
                      onClick={() => navigate('/settings?tab=branding')}
                      className="text-xs text-viraly-orange hover:underline"
                    >
                      Upload →
                    </button>
                  </div>
                </div>
              )}
              
              {/* VISUAL FILTER - Picture Post Enhancement */}
              
              {/* FEATURE YOURSELF - Picture Post Headshot Overlay */}
              {/* Mutual Exclusivity: Hide entire section when Personal Branding is selected */}
              {outputType === "post" && !isTrialUser && postStyle !== "my_brand" && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="card-base p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <UserCircle className="w-5 h-5 text-viraly-orange" />
                      <label className="text-sm font-medium text-viraly-light">Feature Yourself</label>
                    </div>
                    <span className="text-xs px-2 py-1 rounded bg-green-500/20 text-green-400">FREE</span>
                  </div>
                  
                  {/* Feature Yourself options - only shown when NOT using Personal Branding */}
                      <p className="text-xs text-viraly-muted mb-4">
                        Add your photo to the post for a personal touch. 
                        <span className="text-yellow-400"> Tip: Your photo won't overlap text positions you've selected.</span>
                      </p>
                      
                      <div className="grid grid-cols-3 gap-2">
                        <button
                          onClick={() => setFeatureYourself("none")}
                          className={`p-3 rounded-lg border-2 transition-all text-center ${
                            featureYourself === "none"
                              ? "border-viraly-orange bg-viraly-orange/10"
                              : "border-white/10 hover:border-white/20"
                          }`}
                          data-testid="feature-yourself-post-none"
                        >
                          <ImageIcon className="w-5 h-5 mx-auto mb-1 text-viraly-muted" />
                          <p className="text-xs font-medium">No</p>
                          <p className="text-[10px] text-viraly-muted">Content only</p>
                        </button>
                        
                        <button
                          onClick={() => setFeatureYourself("subtle")}
                          className={`p-3 rounded-lg border-2 transition-all text-center ${
                            featureYourself === "subtle"
                              ? "border-viraly-orange bg-viraly-orange/10"
                              : "border-white/10 hover:border-white/20"
                          }`}
                          data-testid="feature-yourself-post-subtle"
                        >
                          <div className="relative w-8 h-8 mx-auto mb-1">
                            <ImageIcon className="w-8 h-8 text-viraly-muted" />
                            <div className="absolute -bottom-1 -left-1 w-4 h-4 rounded-full bg-viraly-orange/50 border border-white/50" />
                          </div>
                          <p className="text-xs font-medium">Subtle</p>
                          <p className="text-[10px] text-viraly-muted">Small headshot</p>
                        </button>
                        
                        <button
                          onClick={() => setFeatureYourself("prominent")}
                          className={`p-3 rounded-lg border-2 transition-all text-center ${
                            featureYourself === "prominent"
                              ? "border-viraly-orange bg-viraly-orange/10"
                              : "border-white/10 hover:border-white/20"
                          }`}
                          data-testid="feature-yourself-post-prominent"
                        >
                          <div className="relative w-8 h-8 mx-auto mb-1">
                            <ImageIcon className="w-8 h-8 text-viraly-muted" />
                            <div className="absolute -bottom-1 -left-1 w-5 h-5 rounded-full bg-viraly-orange border-2 border-white flex items-center justify-center">
                              <User className="w-3 h-3 text-white" />
                            </div>
                          </div>
                          <p className="text-xs font-medium">Prominent</p>
                          <p className="text-[10px] text-viraly-muted">Headshot + name</p>
                        </button>
                      </div>
                  
                      {featureYourself !== "none" && (
                        <div className="mt-4 space-y-3">
                          <p className="text-xs text-viraly-muted">Choose your photo:</p>
                          <div className="flex gap-3">
                            {user?.headshot_url && (
                              <button
                                onClick={() => {
                                  setHeadshotSource("profile");
                                  // Clear upload preview when switching back to profile
                                  if (headshotSource !== "profile") {
                                    setHeadshotPreview(null);
                                  }
                                }}
                                className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                                  headshotSource === "profile" || !headshotSource
                                    ? "border-viraly-orange bg-viraly-orange/10"
                                    : "border-white/10 hover:border-white/20"
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <img 
                                    src={user.headshot_url.startsWith('/') ? `${API_URL}${user.headshot_url}` : user.headshot_url}
                                    alt="Profile"
                                    className="w-8 h-8 rounded-full object-cover border border-white/20"
                                  />
                                  <div className="text-left">
                                    <p className="text-xs font-medium text-viraly-light">Profile Photo</p>
                                    <p className="text-[10px] text-viraly-muted">{user?.name || 'You'}</p>
                                  </div>
                                </div>
                              </button>
                            )}
                            <button
                              onClick={() => document.getElementById('headshot-upload-post')?.click()}
                              className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                                headshotSource === "upload"
                                  ? "border-viraly-orange bg-viraly-orange/10"
                                  : "border-white/10 hover:border-white/20"
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                {headshotPreview ? (
                                  <img 
                                    src={headshotPreview}
                                    alt="Upload"
                                    className="w-8 h-8 rounded-full object-cover border border-white/20"
                                  />
                                ) : (
                                  <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                                    <Upload className="w-4 h-4 text-viraly-muted" />
                                  </div>
                                )}
                                <div className="text-left">
                                  <p className="text-xs font-medium text-viraly-light">Upload New</p>
                                  <p className="text-[10px] text-viraly-muted">Choose file</p>
                                </div>
                              </div>
                            </button>
                            <input
                              type="file"
                              id="headshot-upload-post"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  setHeadshotSource("upload");
                                  setHeadshotFile(file);
                                  const reader = new FileReader();
                                  reader.onload = (ev) => setHeadshotPreview(ev.target?.result);
                                  reader.readAsDataURL(file);
                                }
                              }}
                            />
                          </div>
                        </div>
                      )}
                      
                      {/* Position Selection for Profile Photo */}
                      {featureYourself !== "none" && (user?.headshot_url || headshotPreview) && (
                        <div className="mt-4 space-y-2">
                          <p className="text-xs text-viraly-muted">Where should your photo appear?</p>
                          <p className="text-[10px] text-yellow-400 mb-2">
                            Note: Cannot place on tiles with text fields or bottom-right (Viraly watermark)
                          </p>
                          <div className="grid grid-cols-3 gap-1 max-w-[180px]">
                            {[
                              { id: "top-left", label: "TL" },
                              { id: "top-center", label: "TC" },
                              { id: "top-right", label: "TR" },
                              { id: "center-left", label: "ML" },
                              { id: "center", label: "MC" },
                              { id: "center-right", label: "MR" },
                              { id: "bottom-left", label: "BL" },
                              { id: "bottom-center", label: "BC" },
                              { id: "bottom-right", label: "🔒", disabled: true, reserved: true },
                            ].map(pos => {
                              // Check if this position is occupied by a field
                              // fieldPositions structure: { "position-id": ["field1", "field2"], ... }
                              const isOccupied = Object.keys(fieldPositions || {}).includes(pos.id);
                              const isDisabled = pos.disabled || isOccupied;
                              return (
                                <button
                                  key={pos.id}
                                  onClick={() => !isDisabled && setFeatureYourselfPosition(pos.id)}
                                  disabled={isDisabled}
                                  className={`p-2 rounded text-[10px] font-medium transition-all ${
                                    featureYourselfPosition === pos.id
                                      ? "bg-viraly-orange text-white"
                                      : pos.reserved
                                        ? "bg-viraly-orange/20 text-viraly-orange/70 cursor-not-allowed border border-viraly-orange/30"
                                        : isDisabled
                                          ? "bg-white/5 text-viraly-muted/50 cursor-not-allowed"
                                          : "bg-white/10 text-viraly-muted hover:bg-white/20"
                                  }`}
                                  title={pos.reserved ? "Reserved for Viraly Watermark" : isDisabled ? "Occupied by text field" : pos.id}
                                >
                                  {pos.label}
                                  {isOccupied && !pos.disabled && <span className="block text-[8px]">×</span>}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {!user?.headshot_url && featureYourself !== "none" && !headshotPreview && (
                        <div className="mt-3 p-2 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                          <p className="text-xs text-yellow-400">Upload a photo above or add one in Settings</p>
                        </div>
                      )}
                </motion.div>
              )}
              
              {/* Feature Yourself - Disabled Notice when Personal Branding is active */}
              {outputType === "post" && !isTrialUser && postStyle === "my_brand" && (
                <div className="card-base p-4 opacity-60">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                      <UserCircle className="w-4 h-4 text-viraly-muted" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-viraly-muted">Feature Yourself</p>
                      <p className="text-xs text-viraly-muted/70">
                        Disabled — Your Personal Branding already includes your photo
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Bonus Content Types Banner - Visible, not subtle */}
              <div className="p-4 rounded-xl bg-gradient-to-r from-green-500/10 via-blue-500/10 to-purple-500/10 border border-white/10">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-viraly-orange" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-viraly-light mb-1">
                      Your content can also become:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-green-500/20 text-green-400">
                        <Mail className="w-3 h-3" /> Email Campaign
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-blue-500/20 text-blue-400">
                        <FileText className="w-3 h-3" /> Newsletter
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-purple-500/20 text-purple-400">
                        <Target className="w-3 h-3" /> Ad Creative
                      </span>
                    </div>
                    <p className="text-xs text-viraly-muted mt-2">
                      After generating, click "Create Email" or "Create Ad" on any post — it's FREE!
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Single/Bulk Selection - Show for BOTH posts and videos */}
              {outputType && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="card-base p-6"
                >
                  <label className="text-sm text-viraly-muted mb-3 block">
                    How many {outputType === "video" ? "videos" : "posts"}?
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setScheduleType("single")}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        scheduleType === "single"
                          ? "border-viraly-orange bg-viraly-orange/10"
                          : "border-white/10 hover:border-white/20"
                      }`}
                    >
                      <Layers className="w-5 h-5 mb-2" />
                      <p className="font-medium">Single Post</p>
                      <p className="text-xs text-viraly-muted">Create one post now</p>
                    </button>
                    <button
                      onClick={() => setScheduleType("bulk")}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        scheduleType === "bulk"
                          ? "border-viraly-orange bg-viraly-orange/10"
                          : "border-white/10 hover:border-white/20"
                      }`}
                    >
                      <Calendar className="w-5 h-5 mb-2" />
                      <p className="font-medium">Bulk Schedule</p>
                      <p className="text-xs text-viraly-muted">Create multiple days</p>
                    </button>
                  </div>
                  
                  {scheduleType === "bulk" && (
                    <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-viraly-muted mb-1 block">Days</label>
                        <Input
                          type="number"
                          min="1"
                          max="30"
                          value={days}
                          onChange={(e) => setDays(parseInt(e.target.value) || 1)}
                          className="input-base"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-viraly-muted mb-1 block">{outputType === "video" ? "Videos" : "Posts"}/Day</label>
                        <Input
                          type="number"
                          min="1"
                          max="5"
                          value={postsPerDay}
                          onChange={(e) => setPostsPerDay(parseInt(e.target.value) || 1)}
                          className="input-base"
                        />
                      </div>
                      
                      {/* Info about unique content - context-aware based on input/output type */}
                      <div className="col-span-2 space-y-4">
                        {/* Case: Video input → Posts (same video, different captions) */}
                        {inputType === "video" && outputType === "post" && (
                          <div className="p-3 rounded-lg bg-viraly-orange/10 border border-viraly-orange/30">
                            <p className="text-sm text-viraly-light">
                              Each day gets <span className="font-semibold text-viraly-orange">unique captions & hashtags</span>.
                              <span className="text-xs text-viraly-muted block mt-1">Your video stays the same, we create different social posts for variety.</span>
                            </p>
                          </div>
                        )}
                        
                        {/* Case: Image input → Video output (offer choice) */}
                        {inputType === "image" && outputType === "video" && (
                          <div className="p-3 rounded-lg bg-viraly-orange/10 border border-viraly-orange/30 space-y-3">
                            <p className="text-sm text-viraly-light font-medium">How should we create your bulk videos?</p>
                            <div className="grid grid-cols-2 gap-2">
                              <button
                                type="button"
                                onClick={() => setBulkVideoMode("same")}
                                className={`p-2 rounded-lg text-left transition-all ${
                                  bulkVideoMode === "same"
                                    ? "bg-viraly-orange/20 border-viraly-orange border"
                                    : "bg-white/5 border-white/10 border hover:bg-white/10"
                                }`}
                              >
                                <p className="text-xs font-medium">Same Video, Different Posts</p>
                                <p className="text-[10px] text-viraly-muted">Animate your image once, unique captions daily</p>
                              </button>
                              <button
                                type="button"
                                onClick={() => setBulkVideoMode("inspired")}
                                className={`p-2 rounded-lg text-left transition-all ${
                                  bulkVideoMode === "inspired"
                                    ? "bg-viraly-orange/20 border-viraly-orange border"
                                    : "bg-white/5 border-white/10 border hover:bg-white/10"
                                }`}
                              >
                                <p className="text-xs font-medium">AI-Inspired Variations</p>
                                <p className="text-[10px] text-viraly-muted">AI creates unique videos inspired by your image</p>
                              </button>
                            </div>
                          </div>
                        )}
                        
                        {/* Case: URL/Text input → Video output (always unique videos) */}
                        {(inputType === "url" || inputType === "text") && outputType === "video" && (
                          <div className="p-3 rounded-lg bg-viraly-orange/10 border border-viraly-orange/30">
                            <p className="text-sm text-viraly-light">
                              Each day gets a <span className="font-semibold text-viraly-orange">unique AI-generated video</span>.
                              <span className="text-xs text-viraly-muted block mt-1">AI creates fresh video content for each scheduled day.</span>
                            </p>
                          </div>
                        )}
                        
                        {/* Case: Any input (except video) → Picture Post */}
                        {outputType === "post" && inputType !== "video" && (
                          <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/30 space-y-3">
                            <p className="text-sm text-viraly-light font-medium">Bulk Content Options</p>
                            
                            {/* Content Mode */}
                            <div className="space-y-2">
                              <p className="text-xs text-viraly-muted">Template preference:</p>
                              <div className="grid grid-cols-1 gap-2">
                                <button
                                  type="button"
                                  onClick={() => setBulkContentMode("same_content_same_template")}
                                  className={`p-2 rounded-lg text-left transition-all ${
                                    bulkContentMode === "same_content_same_template"
                                      ? "bg-purple-500/20 border-purple-500 border"
                                      : "bg-white/5 border-white/10 border hover:bg-white/10"
                                  }`}
                                >
                                  <p className="text-xs font-medium">Same Content, Same Template</p>
                                  <p className="text-[10px] text-viraly-muted">Consistent look, unique captions & hashtags daily</p>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setBulkContentMode("same_content_different_template")}
                                  className={`p-2 rounded-lg text-left transition-all ${
                                    bulkContentMode === "same_content_different_template"
                                      ? "bg-purple-500/20 border-purple-500 border"
                                      : "bg-white/5 border-white/10 border hover:bg-white/10"
                                  }`}
                                >
                                  <p className="text-xs font-medium">Same Content, Different Templates</p>
                                  <p className="text-[10px] text-viraly-muted">AI picks different templates each day for variety</p>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setBulkContentMode("different_content_different_template")}
                                  className={`p-2 rounded-lg text-left transition-all ${
                                    bulkContentMode === "different_content_different_template"
                                      ? "bg-purple-500/20 border-purple-500 border"
                                      : "bg-white/5 border-white/10 border hover:bg-white/10"
                                  }`}
                                >
                                  <p className="text-xs font-medium">Different Content, Different Templates</p>
                                  <p className="text-[10px] text-viraly-muted">Customize content for each day</p>
                                </button>
                              </div>
                            </div>
                            
                            {/* EXPANDED CARDS FOR DIFFERENT CONTENT/TEMPLATES */}
                            {(bulkContentMode === "different_content_different_template" || bulkContentMode === "same_content_different_template") && (
                              <div className="space-y-2 pt-3 border-t border-white/10">
                                <div className="flex items-center justify-between">
                                  <p className="text-xs text-viraly-muted">Customize per day:</p>
                                  {Object.keys(bulkDayDetails).length > 0 && bulkContentMode === "different_content_different_template" && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        // Copy Day 1 details to all other days
                                        const day1 = bulkDayDetails[1];
                                        if (day1) {
                                          const newDetails = {};
                                          for (let i = 1; i <= days; i++) {
                                            newDetails[i] = { ...day1 };
                                          }
                                          setBulkDayDetails(newDetails);
                                          toast.success("Copied Day 1 details to all days");
                                        }
                                      }}
                                      className="text-[10px] text-viraly-orange hover:underline"
                                    >
                                      Copy Day 1 to all
                                    </button>
                                  )}
                                </div>
                                
                                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                                  {Array.from({ length: days }, (_, i) => i + 1).map(dayNum => {
                                    // For same_content_different_template: Day 1 uses main template, show days 2-N
                                    // For different_content_different_template: Show all days 1-N
                                    const skipDay1ForSameContent = bulkContentMode === "same_content_different_template" && dayNum === 1;
                                    if (skipDay1ForSameContent) {
                                      // Show Day 1 as "Using main template selected above"
                                      return (
                                        <div key={dayNum} className="rounded-lg border border-green-500/30 bg-green-500/10 p-2">
                                          <div className="flex items-center justify-between">
                                            <span className="text-xs font-medium text-green-400">Day 1</span>
                                            <span className="text-[10px] text-green-400">
                                              {selectedTemplate ? `✓ ${selectedTemplate.name}` : "Using AI Generated design"}
                                            </span>
                                          </div>
                                        </div>
                                      );
                                    }
                                    
                                    const dayData = bulkDayDetails[dayNum] || {};
                                    const isExpanded = expandedBulkDay === dayNum;
                                    const hasDetails = dayData.details && Object.values(dayData.details).some(v => v);
                                    const hasTemplate = dayData.template;
                                    
                                    // For same_content_different_template, show remaining days clearly
                                    const dayLabel = bulkContentMode === "same_content_different_template" 
                                      ? `Day ${dayNum} - Select different template`
                                      : `Day ${dayNum}`;
                                    
                                    return (
                                      <div
                                        key={dayNum}
                                        className={`rounded-lg border transition-all ${
                                          isExpanded 
                                            ? "border-purple-500 bg-purple-500/10" 
                                            : hasTemplate
                                              ? "border-green-500/30 bg-green-500/5"
                                              : "border-yellow-500/30 bg-yellow-500/5"
                                        }`}
                                      >
                                        <button
                                          type="button"
                                          onClick={() => setExpandedBulkDay(isExpanded ? null : dayNum)}
                                          className="w-full p-2 flex items-center justify-between"
                                        >
                                          <div className="flex items-center gap-2">
                                            <span className="text-xs font-medium">
                                              {bulkContentMode === "same_content_different_template" ? `Day ${dayNum}` : `Day ${dayNum}`}
                                            </span>
                                            {hasTemplate ? (
                                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-400">
                                                ✓ {dayData.template.name}
                                              </span>
                                            ) : (
                                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400">
                                                Select template
                                              </span>
                                            )}
                                            {hasDetails && (
                                              <span className="text-[10px] text-green-400">✓</span>
                                            )}
                                          </div>
                                          <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                                        </button>
                                        
                                        {isExpanded && (
                                          <div className="p-3 pt-0 space-y-3">
                                            {/* Template Selection for this day */}
                                            <div>
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  // Open template selector for this specific day
                                                  setShowTemplateSelector(true);
                                                  // Store which day we're selecting for
                                                  window._bulkDaySelectingTemplate = dayNum;
                                                }}
                                                className="w-full p-2 rounded border border-dashed border-white/20 text-xs text-viraly-muted hover:border-purple-500 hover:text-purple-400"
                                              >
                                                {hasTemplate ? `Change: ${dayData.template.name}` : "Select Template"}
                                              </button>
                                            </div>
                                            
                                            {/* Listing Details for this day (only for different content mode) */}
                                            {bulkContentMode === "different_content_different_template" && (
                                              <div className="space-y-2">
                                                <p className="text-[10px] text-viraly-muted">Details for Day {dayNum}:</p>
                                                {["title", "price", "description"].map(field => (
                                                  <input
                                                    key={field}
                                                    type="text"
                                                    placeholder={field.charAt(0).toUpperCase() + field.slice(1)}
                                                    value={dayData.details?.[field] || ""}
                                                    onChange={(e) => {
                                                      setBulkDayDetails(prev => ({
                                                        ...prev,
                                                        [dayNum]: {
                                                          ...prev[dayNum],
                                                          details: {
                                                            ...(prev[dayNum]?.details || {}),
                                                            [field]: e.target.value
                                                          }
                                                        }
                                                      }));
                                                    }}
                                                    className="w-full px-2 py-1 text-xs bg-white/5 border border-white/10 rounded"
                                                  />
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                                
                                {bulkContentMode === "different_content_different_template" && (
                                  <p className="text-[10px] text-yellow-400">
                                    Fill details for all {days} days before proceeding
                                  </p>
                                )}
                              </div>
                            )}
                            
                            {/* Image Mode - Only for image input */}
                            {inputType === "image" && (
                              <div className="space-y-2 pt-2 border-t border-white/10">
                                <p className="text-xs text-viraly-muted">Image preference:</p>
                                <div className="grid grid-cols-2 gap-2">
                                  <button
                                    type="button"
                                    onClick={() => setBulkImageMode("same_image")}
                                    className={`p-2 rounded-lg text-left transition-all ${
                                      bulkImageMode === "same_image"
                                        ? "bg-purple-500/20 border-purple-500 border"
                                        : "bg-white/5 border-white/10 border hover:bg-white/10"
                                    }`}
                                  >
                                    <p className="text-xs font-medium">Use Same Image</p>
                                    <p className="text-[10px] text-viraly-muted">Your original image for all days</p>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setBulkImageMode("inspired_images")}
                                    className={`p-2 rounded-lg text-left transition-all ${
                                      bulkImageMode === "inspired_images"
                                        ? "bg-purple-500/20 border-purple-500 border"
                                        : "bg-white/5 border-white/10 border hover:bg-white/10"
                                    }`}
                                  >
                                    <p className="text-xs font-medium">AI-Inspired Images</p>
                                    <p className="text-[10px] text-viraly-muted">Day 1 = your image, rest = AI-inspired</p>
                                  </button>
                                </div>
                              </div>
                            )}
                            
                            <p className="text-[10px] text-viraly-muted pt-1">
                              All {days} day(s) will have unique captions & hashtags regardless of your choice.
                            </p>
                          </div>
                        )}
                      </div>
                      
                      {/* Credits Summary */}
                      <p className="col-span-2 text-xs text-viraly-muted">
                        {outputType === "video" ? (
                          inputType === "video" ? (
                            // Video input → just post credits (user's video, we create posts)
                            <>Total: <span className="font-semibold text-white">{totalCreditsNeeded} post credit{totalCreditsNeeded > 1 ? 's' : ''}</span> for {days} day{days > 1 ? 's' : ''} ({hasEnoughPostCredits ? 
                              <span className="text-viraly-success">You have enough</span> : 
                              <span className="text-red-400">Need {totalCreditsNeeded - postsRemaining} more</span>
                            })</>
                          ) : (
                            // Image/URL/Text → Video: ALL video credits with selected duration/quality
                            <>Total: <span className="font-semibold text-white">{totalVideoCreditsNeeded} video credit{totalVideoCreditsNeeded > 1 ? 's' : ''}</span> ({getVideoCreditsNeededForSelection()} per video × {totalPosts}) for {days} day{days > 1 ? 's' : ''} ({hasEnoughVideoCredits ? 
                              <span className="text-viraly-success">You have enough</span> : 
                              <span className="text-red-400">Need {totalVideoCreditsNeeded - totalVideoCredits} more</span>
                            })</>
                          )
                        ) : (
                          <>Total: <span className="font-semibold text-white">{totalCreditsNeeded} post credit{totalCreditsNeeded > 1 ? 's' : ''}</span> for {days} day{days > 1 ? 's' : ''} ({hasEnoughPostCredits ? 
                            <span className="text-viraly-success">You have enough</span> : 
                            <span className="text-red-400">Need {totalCreditsNeeded - postsRemaining} more</span>
                          })</>
                        )}
                      </p>
                    </div>
                  )}
                </motion.div>
              )}
              
              {/* Navigation */}
              <div className="flex justify-between">
                <Button onClick={() => setStep(1)} variant="ghost" className="text-viraly-muted">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={() => setStep(3)}
                  disabled={!isStep2Complete()}
                  className="btn-primary px-8"
                  data-testid="step2-next"
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}
          
          {/* STEP 3: Platforms, Branding, Language */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center mb-6">
                <h2 className="font-heading text-xl font-bold">Final Settings</h2>
                <p className="text-viraly-muted text-sm">Platforms, branding, and language</p>
              </div>
              
              {/* Platform Selection */}
              <div className="card-base p-6">
                <label className="text-sm text-viraly-muted mb-3 block">Select Platforms</label>
                
                {connectedPlatforms.length === 0 ? (
                  // No platforms connected - show message
                  <div className="text-center py-6 border border-dashed border-white/20 rounded-xl">
                    <p className="text-viraly-muted mb-2">No social platforms connected yet</p>
                    <p className="text-xs text-viraly-muted mb-4">
                      Connect your accounts to post content. It's <span className="text-viraly-orange font-semibold">FREE</span> to post to multiple platforms!
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate("/settings?tab=social")}
                      className="border-viraly-orange text-viraly-orange hover:bg-viraly-orange/10"
                    >
                      Connect Platforms
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex gap-3">
                      {platforms.map((p) => {
                        const isConnected = connectedPlatforms.includes(p.id);
                        return (
                          <button
                            key={p.id}
                            onClick={() => {
                              if (!isConnected) return; // Can't select unconnected platform
                              if (selectedPlatforms.includes(p.id)) {
                                if (selectedPlatforms.length > 1) {
                                  setSelectedPlatforms(selectedPlatforms.filter(id => id !== p.id));
                                }
                              } else {
                                setSelectedPlatforms([...selectedPlatforms, p.id]);
                              }
                            }}
                            disabled={!isConnected}
                            className={`flex-1 p-4 rounded-xl border-2 transition-all relative ${
                              !isConnected 
                                ? "border-white/5 opacity-40 cursor-not-allowed"
                                : selectedPlatforms.includes(p.id)
                                  ? "border-viraly-orange bg-viraly-orange/10"
                                  : "border-white/10 hover:border-white/20"
                            }`}
                          >
                            {!isConnected && (
                              <div className="absolute -top-2 -right-2 bg-white/10 text-[10px] px-2 py-0.5 rounded-full">
                                Not connected
                              </div>
                            )}
                            <div 
                              className="w-8 h-8 rounded-full mx-auto mb-2 flex items-center justify-center"
                              style={{ backgroundColor: isConnected ? p.color : "#444" }}
                            >
                              {p.name[0]}
                            </div>
                            <p className="text-sm font-medium">{p.name}</p>
                          </button>
                        );
                      })}
                    </div>
                    
                    {/* Prompt to connect more platforms */}
                    {connectedPlatforms.length < 3 && (
                      <div className="mt-4 p-3 bg-viraly-orange/10 border border-viraly-orange/20 rounded-lg">
                        <p className="text-sm text-viraly-orange">
                          💡 Connect more platforms to reach a wider audience - it's <span className="font-semibold">FREE</span>! 
                          Same content, multiple channels, no extra cost.
                        </p>
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => navigate("/settings?tab=social")}
                          className="text-viraly-orange p-0 h-auto mt-1"
                        >
                          Connect {3 - connectedPlatforms.length} more platform{connectedPlatforms.length < 2 ? 's' : ''} →
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
              
              {/* Platform Permissions Checklist - One-time consent for TikTok/Pinterest */}
              {/* Shows only when TikTok or Pinterest is selected AND user hasn't given consent yet */}
              {needsPlatformPermissions() && (
                <PlatformPermissionsChecklist
                  selectedPlatforms={selectedPlatforms}
                  onPermissionsComplete={handlePlatformPermissionsComplete}
                />
              )}
              
              {/* TikTok Settings Panel - Shows after one-time consent for per-post customization */}
              {/* Allows users to override their saved defaults for specific posts */}
              {isTikTokSelected && platformPermissionsGranted.tiktok === true && (
                <div className="card-base p-6">
                  <label className="text-sm text-viraly-muted mb-3 block flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-black flex items-center justify-center text-xs font-bold">TT</span>
                    TikTok Settings
                    <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full ml-auto">
                      Condensed
                    </span>
                  </label>
                  <p className="text-xs text-viraly-muted mb-3">
                    Privacy and brand disclosure settings for TikTok posting
                  </p>
                  <TikTokCondensedSettings
                    settings={tiktokSettings}
                    onSettingsChange={setTiktokSettings}
                  />
                </div>
              )}
              
              {/* Video Settings Summary - Only show for video output */}
              {outputType === "video" && (
                <div className="card-base p-6">
                  <label className="text-sm text-viraly-muted mb-3 block">Video Configuration</label>
                  <div className="p-4 rounded-xl border-2 border-purple-500/50 bg-purple-500/10">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-lg">{videoDuration} seconds</p>
                        <p className="text-sm text-viraly-muted">{videoQuality === "1080p" ? "HD 1080p" : "720p"}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-purple-400">{getVideoCreditsNeededForSelection()}</p>
                        <p className="text-xs text-viraly-muted">credit{getVideoCreditsNeededForSelection() > 1 ? 's' : ''}/video</p>
                      </div>
                    </div>
                    {totalVideoCreditsNeeded > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/10 flex justify-between items-center">
                        <span className="text-sm text-viraly-muted">Total for this job:</span>
                        <span className={`font-bold ${hasEnoughVideoCredits ? 'text-viraly-success' : 'text-red-400'}`}>
                          {totalVideoCreditsNeeded} credits {hasEnoughVideoCredits ? '✓' : `(need ${totalVideoCreditsNeeded - totalVideoCredits} more)`}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Branding - Disabled when using template */}
              <div className="card-base p-6">
                <label className="text-sm text-viraly-muted mb-3 block">Post Style</label>
                
                {/* Template Selection Notice */}
                {selectedTemplate && (
                  <div className="mb-4 p-3 rounded-xl bg-purple-500/10 border border-purple-500/30">
                    <div className="flex items-center gap-2">
                      <Layout className="w-4 h-4 text-purple-400" />
                      <span className="text-sm text-purple-400">Using Template: <strong>{selectedTemplate.name}</strong></span>
                    </div>
                    <p className="text-xs text-viraly-muted mt-1">
                      Personal branding is not applied when using templates. The template's design will be used instead.
                    </p>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => {
                      setPostStyle("generic");
                      // Restore previously saved Feature Yourself selection
                      if (savedFeatureYourself && savedFeatureYourself !== "none") {
                        setFeatureYourself(savedFeatureYourself);
                      }
                    }}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      postStyle === "generic"
                        ? "border-viraly-orange bg-viraly-orange/10"
                        : "border-white/10 hover:border-white/20"
                    }`}
                  >
                    <Sparkles className="w-5 h-5 mb-2" />
                    <p className="font-medium">{selectedTemplate ? "Template Style" : "Generic"}</p>
                    <p className="text-xs text-viraly-muted">{selectedTemplate ? selectedTemplate.name : "Standard AI style"}</p>
                  </button>
                  <button
                    onClick={() => {
                      if (!selectedTemplate) {
                        setPostStyle("my_brand");
                        // Save current Feature Yourself selection before disabling
                        if (featureYourself !== "none") {
                          setSavedFeatureYourself(featureYourself);
                        }
                        setFeatureYourself("none"); // Mutual exclusivity: disable Feature Yourself
                      }
                    }}
                    disabled={!hasBrandAssets || selectedTemplate}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      postStyle === "my_brand"
                        ? "border-viraly-orange bg-viraly-orange/10"
                        : (!hasBrandAssets || selectedTemplate)
                          ? "border-white/5 opacity-50 cursor-not-allowed"
                          : "border-white/10 hover:border-white/20"
                    }`}
                  >
                    <Globe className="w-5 h-5 mb-2" />
                    <p className="font-medium">Personal Brand</p>
                    <p className="text-xs text-viraly-muted">
                      {selectedTemplate 
                        ? "Not available with templates" 
                        : hasBrandAssets 
                          ? "Your branding overlay" 
                          : "Set up in Settings"}
                    </p>
                  </button>
                </div>
                
                {/* Branding Style Selection - Expands when Personal Brand is selected */}
                {postStyle === "my_brand" && (
                  <div className="mt-4 p-4 rounded-xl bg-white/5 border border-white/10">
                    <label className="text-sm text-viraly-muted mb-3 block">Choose branding visibility:</label>
                    <div className="grid grid-cols-2 gap-3">
                      {/* Subtle Option */}
                      <button
                        onClick={() => setBrandingStyle("subtle")}
                        className={`p-3 rounded-lg border-2 transition-all text-left ${
                          brandingStyle === "subtle"
                            ? "border-viraly-orange bg-viraly-orange/10"
                            : "border-white/10 hover:border-white/20"
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            brandingStyle === "subtle" ? "border-viraly-orange" : "border-white/30"
                          }`}>
                            {brandingStyle === "subtle" && (
                              <div className="w-2 h-2 rounded-full bg-viraly-orange" />
                            )}
                          </div>
                          <span className="font-medium text-sm">Subtle</span>
                        </div>
                        {/* Mini Preview */}
                        <div className="bg-gradient-to-b from-gray-700 to-gray-800 rounded h-24 relative overflow-hidden">
                          <div className="absolute inset-x-0 bottom-0 h-6 bg-gray-900/90 flex items-center px-2 gap-2">
                            <div className="w-4 h-4 rounded bg-gray-600" />
                            <span className="text-[8px] text-gray-400 truncate">📞 (XXX) XXX-XXXX • website.com</span>
                          </div>
                        </div>
                        <p className="text-[10px] text-viraly-muted mt-2">Photo + phone + website in small footer</p>
                      </button>
                      
                      {/* Prominent Option */}
                      <button
                        onClick={() => setBrandingStyle("prominent")}
                        className={`p-3 rounded-lg border-2 transition-all text-left ${
                          brandingStyle === "prominent"
                            ? "border-viraly-orange bg-viraly-orange/10"
                            : "border-white/10 hover:border-white/20"
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            brandingStyle === "prominent" ? "border-viraly-orange" : "border-white/30"
                          }`}>
                            {brandingStyle === "prominent" && (
                              <div className="w-2 h-2 rounded-full bg-viraly-orange" />
                            )}
                          </div>
                          <span className="font-medium text-sm">Prominent</span>
                        </div>
                        {/* Mini Preview */}
                        <div className="bg-gradient-to-b from-gray-700 to-gray-800 rounded h-24 relative overflow-hidden">
                          <div className="absolute inset-x-0 bottom-0 h-12 bg-gray-900/95 p-1">
                            <div className="flex gap-2 h-full">
                              <div className="w-8 h-8 rounded-full bg-gray-600 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="text-[7px] text-white font-bold truncate">Your Name</div>
                                <div className="text-[6px] text-gray-400 truncate">📞 (XXX) • 📧 email</div>
                                <div className="text-[6px] text-gray-400 truncate">🌐 website • License</div>
                              </div>
                              <div className="w-6 h-6 bg-gray-600 rounded flex-shrink-0" />
                            </div>
                          </div>
                        </div>
                        <p className="text-[10px] text-viraly-muted mt-2">Full footer with photo, name, all contacts</p>
                      </button>
                    </div>
                    <p className="text-xs text-viraly-muted/70 mt-3 text-center">
                      💡 You can switch between styles FREE after generation
                    </p>
                  </div>
                )}
              </div>
              
              {/* Language */}
              <div className="card-base p-6">
                <label className="text-sm text-viraly-muted mb-3 block">Content Language</label>
                <select
                  value={contentLanguage}
                  onChange={(e) => setContentLanguage(e.target.value)}
                  className="input-base w-full"
                >
                  {languageOptions.map((lang) => (
                    <option key={lang.id} value={lang.id}>
                      {lang.flag} {lang.name}
                    </option>
                  ))}
                </select>
                {contentLanguage !== "en" && (
                  <p className="text-xs text-yellow-400/80 mt-2 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    AI translations may contain errors. Please review before publishing.
                  </p>
                )}
              </div>
              
              {/* Post Timing Selection - AI Suggested vs Custom Time */}
              {scheduleType === "single" && (
                <div className="card-base p-6">
                  <label className="text-sm text-viraly-muted mb-3 block flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    When should we post?
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setScheduleTimePreference("ai_suggested")}
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        scheduleTimePreference === "ai_suggested"
                          ? "border-viraly-orange bg-viraly-orange/10"
                          : "border-white/10 hover:border-white/20"
                      }`}
                      data-testid="schedule-time-ai"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className={`w-5 h-5 ${scheduleTimePreference === "ai_suggested" ? "text-viraly-orange" : "text-viraly-muted"}`} />
                        <span className="font-medium">AI Suggested Time</span>
                      </div>
                      <p className="text-xs text-viraly-muted">
                        Let AI pick the optimal posting time based on your audience engagement patterns
                      </p>
                      {scheduleTimePreference === "ai_suggested" && (
                        <div className="mt-2 text-xs text-viraly-orange flex items-center gap-1">
                          <Check className="w-3 h-3" /> Recommended for best engagement
                        </div>
                      )}
                    </button>
                    
                    <button
                      onClick={() => setScheduleTimePreference("custom")}
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        scheduleTimePreference === "custom"
                          ? "border-viraly-orange bg-viraly-orange/10"
                          : "border-white/10 hover:border-white/20"
                      }`}
                      data-testid="schedule-time-custom"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className={`w-5 h-5 ${scheduleTimePreference === "custom" ? "text-viraly-orange" : "text-viraly-muted"}`} />
                        <span className="font-medium">Choose My Time</span>
                      </div>
                      <p className="text-xs text-viraly-muted">
                        Select a specific time to publish your content
                      </p>
                    </button>
                  </div>
                  
                  {/* Custom Time Picker - Shows when "Choose My Time" is selected */}
                  {scheduleTimePreference === "custom" && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      className="mt-4 p-4 rounded-xl bg-white/5 border border-white/10"
                    >
                      <label className="text-sm text-viraly-muted mb-3 block">Select posting time:</label>
                      
                      {/* Beautiful Time Picker */}
                      <div className="flex items-center gap-3 flex-wrap">
                        {/* Hour Selector */}
                        <div className="flex flex-col items-center">
                          <span className="text-[10px] text-viraly-muted mb-1">Hour</span>
                          <select
                            value={(() => {
                              const [h] = (customScheduleTime || "12:00").split(":");
                              const hour24 = parseInt(h);
                              if (hour24 === 0) return "12";
                              if (hour24 > 12) return String(hour24 - 12);
                              return String(hour24);
                            })()}
                            onChange={(e) => {
                              const [, m] = (customScheduleTime || "12:00").split(":");
                              const [h] = (customScheduleTime || "12:00").split(":");
                              const currentHour24 = parseInt(h);
                              const isPM = currentHour24 >= 12;
                              let newHour = parseInt(e.target.value);
                              if (isPM) {
                                newHour = newHour === 12 ? 12 : newHour + 12;
                              } else {
                                newHour = newHour === 12 ? 0 : newHour;
                              }
                              setCustomScheduleTime(`${String(newHour).padStart(2, "0")}:${m}`);
                            }}
                            className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-viraly-light text-center appearance-none cursor-pointer hover:bg-white/15 transition-colors w-16"
                          >
                            {[12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((h) => (
                              <option key={h} value={h}>{h}</option>
                            ))}
                          </select>
                        </div>
                        
                        <span className="text-2xl text-viraly-muted font-bold mt-4">:</span>
                        
                        {/* Minute Selector */}
                        <div className="flex flex-col items-center">
                          <span className="text-[10px] text-viraly-muted mb-1">Minute</span>
                          <select
                            value={(() => {
                              const [, m] = (customScheduleTime || "12:00").split(":");
                              return m || "00";
                            })()}
                            onChange={(e) => {
                              const [h] = (customScheduleTime || "12:00").split(":");
                              setCustomScheduleTime(`${h}:${e.target.value}`);
                            }}
                            className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-viraly-light text-center appearance-none cursor-pointer hover:bg-white/15 transition-colors w-16"
                          >
                            {["00", "15", "30", "45"].map((m) => (
                              <option key={m} value={m}>{m}</option>
                            ))}
                          </select>
                        </div>
                        
                        {/* AM/PM Toggle */}
                        <div className="flex flex-col items-center">
                          <span className="text-[10px] text-viraly-muted mb-1">Period</span>
                          <div className="flex rounded-lg overflow-hidden border border-white/20">
                            <button
                              type="button"
                              onClick={() => {
                                const [h, m] = (customScheduleTime || "12:00").split(":");
                                let hour = parseInt(h);
                                if (hour >= 12) {
                                  hour = hour === 12 ? 0 : hour - 12;
                                }
                                setCustomScheduleTime(`${String(hour).padStart(2, "0")}:${m}`);
                              }}
                              className={`px-3 py-2 text-sm font-medium transition-colors ${
                                parseInt((customScheduleTime || "12:00").split(":")[0]) < 12
                                  ? "bg-viraly-orange text-white"
                                  : "bg-white/10 text-viraly-muted hover:bg-white/15"
                              }`}
                            >
                              AM
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const [h, m] = (customScheduleTime || "12:00").split(":");
                                let hour = parseInt(h);
                                if (hour < 12) {
                                  hour = hour === 0 ? 12 : hour + 12;
                                }
                                setCustomScheduleTime(`${String(hour).padStart(2, "0")}:${m}`);
                              }}
                              className={`px-3 py-2 text-sm font-medium transition-colors ${
                                parseInt((customScheduleTime || "12:00").split(":")[0]) >= 12
                                  ? "bg-viraly-orange text-white"
                                  : "bg-white/10 text-viraly-muted hover:bg-white/15"
                              }`}
                            >
                              PM
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Display formatted time */}
                      <div className="mt-4 p-3 bg-viraly-orange/10 rounded-lg border border-viraly-orange/20">
                        <p className="text-sm text-viraly-light">
                          <Clock className="w-4 h-4 inline mr-2 text-viraly-orange" />
                          Your post will be scheduled for{" "}
                          <span className="font-bold text-viraly-orange">
                            {(() => {
                              const [h, m] = (customScheduleTime || "12:00").split(":");
                              const hour24 = parseInt(h);
                              const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
                              const ampm = hour24 >= 12 ? "PM" : "AM";
                              return `${hour12}:${m} ${ampm}`;
                            })()}
                          </span>
                          {" "}(your local time)
                        </p>
                      </div>
                      
                      {/* Quick time suggestions */}
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className="text-xs text-viraly-muted">Quick:</span>
                        {[
                          { label: "9 AM", value: "09:00" },
                          { label: "12 PM", value: "12:00" },
                          { label: "3 PM", value: "15:00" },
                          { label: "6 PM", value: "18:00" },
                          { label: "9 PM", value: "21:00" },
                        ].map((t) => (
                          <button
                            key={t.value}
                            type="button"
                            onClick={() => setCustomScheduleTime(t.value)}
                            className={`text-xs px-2 py-1 rounded transition-colors ${
                              customScheduleTime === t.value
                                ? "bg-viraly-orange text-white"
                                : "bg-white/10 text-viraly-muted hover:bg-white/20"
                            }`}
                          >
                            {t.label}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </div>
              )}
              
              {/* Navigation */}
              <div className="flex justify-between">
                <Button onClick={() => setStep(2)} variant="ghost" className="text-viraly-muted">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                {/* Hide main generate button when confirmation modal is showing */}
                {!showConfirmation && (
                  <Button
                    onClick={handleConfirmAndGenerate}
                    disabled={!isStep3Complete() || isScrapingUrl}
                    className="btn-primary px-8"
                    data-testid="generate-btn"
                  >
                    {isScrapingUrl ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing URL...
                      </>
                    ) : needsConfirmation() ? (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Confirm & Generate {outputType === "video" ? "Video" : `${totalPosts} Post${totalPosts > 1 ? "s" : ""}`}
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate {outputType === "video" ? "Video" : `${totalPosts} Post${totalPosts > 1 ? "s" : ""}`}
                      </>
                    )}
                  </Button>
                )}
              </div>
              
              {/* Confirmation Preview Modal */}
              {showConfirmation && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 p-4 rounded-xl bg-viraly-orange/10 border border-viraly-orange/30"
                >
                  <h4 className="font-semibold text-viraly-orange mb-3 flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    Preview & Confirm
                  </h4>
                  
                  {/* URL Scraped Data Preview */}
                  {scrapedData && (
                    <div className="mb-4 p-3 rounded-lg bg-white/5">
                      <p className="text-xs text-viraly-muted mb-1">Extracted from URL:</p>
                      <p className="font-medium text-sm">{scrapedData.title}</p>
                      {scrapedData.description && (
                        <p className="text-xs text-viraly-muted mt-1">{scrapedData.description.substring(0, 150)}...</p>
                      )}
                      {scrapedData.features?.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-viraly-muted">Key points:</p>
                          <ul className="text-xs mt-1 space-y-0.5">
                            {scrapedData.features.slice(0, 3).map((f, i) => (
                              <li key={i} className="text-viraly-muted">• {f.substring(0, 60)}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Voiceover Script Editor - PROMINENT SECTION */}
                  {outputType === 'video' && (
                    <div className="mb-4 p-4 rounded-xl bg-gradient-to-r from-viraly-primary/10 to-purple-500/10 border border-viraly-primary/30">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-lg bg-viraly-primary/20 flex items-center justify-center">
                          <svg className="w-4 h-4 text-viraly-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-white">Video Voiceover Script</p>
                          <p className="text-xs text-viraly-muted">Choose how your video speaks to your audience</p>
                        </div>
                      </div>
                      
                      {/* Script Options */}
                      <div className="grid grid-cols-2 gap-2 mb-3">
                        <button
                          type="button"
                          onClick={() => {
                            // Keep AI-generated script
                            if (!voiceoverPreview) {
                              toast.info("AI will generate the perfect script based on your content");
                            }
                          }}
                          className={`p-3 rounded-lg border text-left transition-all ${
                            !voiceoverPreview 
                              ? 'border-viraly-primary bg-viraly-primary/10 text-white' 
                              : 'border-white/10 bg-white/5 text-viraly-muted hover:border-white/20'
                          }`}
                        >
                          <p className="text-xs font-medium">AI Generated</p>
                          <p className="text-xs text-viraly-muted mt-0.5">Let our AI write the script</p>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (!voiceoverPreview) {
                              setVoiceoverPreview("Type your exact words here. Be concise and engaging!");
                            }
                          }}
                          className={`p-3 rounded-lg border text-left transition-all ${
                            voiceoverPreview 
                              ? 'border-viraly-primary bg-viraly-primary/10 text-white' 
                              : 'border-white/10 bg-white/5 text-viraly-muted hover:border-white/20'
                          }`}
                        >
                          <p className="text-xs font-medium">Custom Script</p>
                          <p className="text-xs text-viraly-muted mt-0.5">Write your own words</p>
                        </button>
                      </div>
                      
                      {/* Script Editor - Shows when Custom is selected or AI has generated */}
                      {voiceoverPreview && (
                        <div className="space-y-2">
                          <Textarea
                            value={voiceoverPreview}
                            onChange={(e) => setVoiceoverPreview(e.target.value)}
                            className="input-base text-sm bg-black/30 border-white/20 focus:border-viraly-primary"
                            rows={4}
                            placeholder="Write exactly what you want the voiceover to say..."
                          />
                          <div className="flex items-center justify-between text-xs">
                            <p className="text-viraly-muted">
                              {voiceoverPreview.split(' ').filter(w => w).length} / {videoDuration * 2} words 
                              <span className="ml-1">({videoDuration}s video)</span>
                            </p>
                            <button
                              type="button"
                              onClick={() => setVoiceoverPreview("")}
                              className="text-viraly-muted hover:text-white transition-colors"
                            >
                              Clear & use AI
                            </button>
                          </div>
                          {voiceoverPreview.split(' ').filter(w => w).length > videoDuration * 2 && (
                            <p className="text-xs text-yellow-400 flex items-center gap-1">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                              Script is too long - will be auto-trimmed to fit
                            </p>
                          )}
                        </div>
                      )}
                      
                      {/* Voice Selection */}
                      <div className="mt-4 pt-4 border-t border-white/10">
                        <p className="text-xs text-viraly-muted mb-2">Voice Style</p>
                        <div className="grid grid-cols-4 gap-2">
                          {voiceOptions.slice(0, 4).map((voice) => (
                            <button
                              key={voice.id}
                              type="button"
                              onClick={() => setSelectedVoice(voice.id)}
                              className={`p-2 rounded-lg border text-center transition-all ${
                                selectedVoice === voice.id
                                  ? 'border-viraly-primary bg-viraly-primary/10'
                                  : 'border-white/10 bg-white/5 hover:border-white/20'
                              }`}
                            >
                              <p className="text-xs font-medium text-white">{voice.name}</p>
                              <p className="text-[10px] text-viraly-muted">{voice.description}</p>
                              {voice.recommended && (
                                <span className="text-[9px] text-viraly-primary">Recommended</span>
                              )}
                            </button>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            // Show all voices in a modal or expand
                            const allVoices = voiceOptions.map(v => `${v.name}: ${v.description}`).join('\n');
                            toast.info(`Available voices:\n${voiceOptions.map(v => v.name).join(', ')}`);
                          }}
                          className="mt-2 text-xs text-viraly-primary hover:underline"
                        >
                          See all {voiceOptions.length} voices
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Listing Details Summary */}
                  {showListingDetails && Object.keys(listingDetails).length > 0 && (
                    <div className="mb-4 p-3 rounded-lg bg-white/5">
                      <p className="text-xs text-viraly-muted mb-1">Listing details will appear on your {outputType === 'video' ? 'post caption' : 'image'}:</p>
                      <div className="text-xs space-y-0.5">
                        {Object.entries(listingDetails).filter(([k, v]) => v).map(([key, value]) => (
                          <p key={key} className="text-viraly-muted">
                            <span className="capitalize">{key.replace('_', ' ')}</span>: {value}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Template Preview - Viral Score & Suggestions */}
                  {templatePreview && (
                    <div className="mb-4 p-3 rounded-lg bg-white/5">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-viraly-muted">Content Analysis</p>
                        {templatePreview.viral_score && (
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            templatePreview.viral_score.overall >= 80 ? 'bg-viraly-success/20 text-viraly-success' :
                            templatePreview.viral_score.overall >= 60 ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            Viral Score: {templatePreview.viral_score.overall}%
                          </span>
                        )}
                      </div>
                      
                      {templatePreview.caption_suggestions?.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-viraly-muted mb-1">Suggested hooks:</p>
                          <ul className="text-xs space-y-1">
                            {templatePreview.caption_suggestions.slice(0, 2).map((suggestion, i) => (
                              <li key={i} className="text-viraly-light">• {suggestion.substring(0, 80)}...</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      <p className="text-xs text-viraly-muted mt-2">
                        Credits needed: <span className="text-viraly-orange font-medium">{templatePreview.credits_needed || 1}</span>
                      </p>
                    </div>
                  )}
                  
                  {/* Loading Preview */}
                  {isLoadingPreview && (
                    <div className="mb-4 p-3 rounded-lg bg-white/5 flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-viraly-orange" />
                      <span className="text-sm text-viraly-muted">Analyzing content...</span>
                    </div>
                  )}
                  
                  <div className="flex gap-2 justify-end">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        setShowConfirmation(false);
                        setScrapedData(null);
                      }}
                      className="text-viraly-muted"
                    >
                      Cancel
                    </Button>
                    <Button 
                      size="sm"
                      onClick={() => {
                        setShowConfirmation(false);
                        handleGenerate();
                      }}
                      className="btn-primary"
                      data-testid="confirm-generate-btn"
                    >
                      <Sparkles className="w-3 h-3 mr-1" />
                      Confirm & Generate
                    </Button>
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}
          
          {/* STEP 4: Generating */}
          {step === 4 && (
            <GeneratingStep
              generationProgress={generationProgress}
              outputType={outputType}
              getProgressMessage={getProgressMessage}
            />
          )}
          
          {/* STEP 5: Done */}
          {step === 5 && (
            <CompletionStep
              outputType={outputType}
              generatedPosts={generatedPosts}
              generatedVideo={generatedVideo}
              platform={selectedPlatforms[0] || "instagram"}
              hasBranding={postStyle === "my_brand"}
              postId={generatedPosts[0]?.id || generatedPosts[0]?.post_id || sessionStorage.getItem('viraly_last_created_post')}
              onViewScheduled={() => {
                const postId = generatedPosts[0]?.id || generatedPosts[0]?.post_id || sessionStorage.getItem('viraly_last_created_post');
                console.log('Navigating to scheduled with postId:', postId);
                // Use window.location for more reliable navigation
                if (postId) {
                  window.location.href = `/posts?tab=scheduled&highlight=${postId}`;
                } else {
                  window.location.href = "/posts?tab=scheduled";
                }
              }}
              onCreateAnother={() => {
                setStep(1);
                setInputType(null);
                setOutputType(null);
                setInputUrl("");
                setInputText("");
                clearFile();
                setGeneratedPosts([]);
                setGeneratedVideo(null);
              }}
            />
          )}
        </AnimatePresence>
      </div>
      
      {/* Video Credits Modal */}
      <VideoCreditsModal
        isOpen={showVideoCreditsModal}
        onClose={() => setShowVideoCreditsModal(false)}
        creditsNeeded={totalVideoCreditsNeeded}
        creditsAvailable={totalVideoCredits}
        videoType={getVideoTypeKey()}
        options={insufficientCreditsOptions}
        userPlan={userPlan}
        onAction={(action, param) => {
          if (action === 'buy') {
            navigate(`/pricing?tab=payg&highlight=${param}`);
          } else if (action === 'switch') {
            // Switch to a different video type
            const [dur, qual] = param.split('_');
            setVideoDuration(parseInt(dur));
            setVideoQuality(qual);
            setShowVideoCreditsModal(false);
          }
        }}
      />
      
      {/* Post Credits Insufficient Modal */}
      {showPostCreditsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md mx-4 bg-viraly-card border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
          >
            <div className="p-6 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold">Need More Post Credits</h3>
                  <p className="text-sm text-viraly-muted">
                    You need {postCreditsNeeded} more credit{postCreditsNeeded > 1 ? 's' : ''} to create this content
                  </p>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between p-3 rounded-xl bg-white/5">
                <span className="text-viraly-muted">Posts needed</span>
                <span className="font-semibold">{totalCreditsNeeded}</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-xl bg-white/5">
                <span className="text-viraly-muted">You have</span>
                <span className="font-semibold text-viraly-orange">{postsRemaining}</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-xl bg-red-500/10 border border-red-500/30">
                <span className="text-red-400">Need more</span>
                <span className="font-bold text-red-400">{postCreditsNeeded} credits</span>
              </div>
              
              <div className="pt-4 border-t border-white/10 space-y-3">
                <p className="text-sm font-medium">Quick Options:</p>
                
                <button
                  onClick={() => {
                    setShowPostCreditsModal(false);
                    navigate("/pricing?tab=payg&item=posts");
                  }}
                  className="w-full p-4 rounded-xl bg-viraly-orange text-white font-semibold hover:bg-viraly-orange/90 transition-all flex items-center justify-center gap-2"
                >
                  <ShoppingCart className="w-5 h-5" />
                  Buy Post Credits (from $5)
                </button>
                
                <button
                  onClick={() => {
                    setShowPostCreditsModal(false);
                    navigate("/pricing");
                  }}
                  className="w-full p-4 rounded-xl bg-purple-500/20 text-purple-400 font-semibold hover:bg-purple-500/30 transition-all flex items-center justify-center gap-2"
                >
                  <Crown className="w-5 h-5" />
                  Upgrade Plan
                </button>
                
                {totalCreditsNeeded > 1 && postsRemaining > 0 && (
                  <button
                    onClick={() => {
                      // Allow partial generation with available credits
                      setShowPostCreditsModal(false);
                      toast.info(`Will create ${postsRemaining} posts with your available credits`);
                      // Adjust bulk days to match available credits
                      if (scheduleType === "bulk") {
                        setBulkDays(postsRemaining);
                        setTimeout(() => handleGenerate(), 100);
                      }
                    }}
                    className="w-full p-4 rounded-xl border border-white/20 text-viraly-muted font-medium hover:border-white/30 hover:bg-white/5 transition-all"
                  >
                    Create {postsRemaining} posts instead
                  </button>
                )}
              </div>
            </div>
            
            <div className="p-4 border-t border-white/10 bg-white/5">
              <button
                onClick={() => setShowPostCreditsModal(false)}
                className="w-full py-2 text-sm text-viraly-muted hover:text-white transition-colors"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </div>
      )}
      
      {/* Voiceover Trim Modal */}
      {showVoiceoverTrimModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-viraly-card border border-white/10 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                <Scissors className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold">Voiceover Too Long</h3>
                <p className="text-sm text-viraly-muted">
                  Your voiceover is {originalVoiceoverDuration}s but video is {videoDuration}s
                </p>
              </div>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => {
                  // Auto-trim voiceover
                  setShowVoiceoverTrimModal(false);
                  toast.info("Voiceover will be auto-trimmed to fit");
                  handleGenerate();
                }}
                className="w-full p-4 rounded-xl border-2 border-white/10 hover:border-viraly-orange/50 hover:bg-viraly-orange/10 transition-all text-left"
              >
                <div className="flex items-center gap-3">
                  <Scissors className="w-5 h-5 text-viraly-orange" />
                  <div>
                    <p className="font-medium">Auto-trim voiceover</p>
                    <p className="text-xs text-viraly-muted">Cut to {videoDuration}s from the start</p>
                  </div>
                </div>
              </button>
              
              <button
                onClick={() => {
                  // Use AI voice instead
                  setUserVoiceoverFile(null);
                  setShowVoiceoverTrimModal(false);
                  toast.info("Using AI-generated voice instead");
                  handleGenerate();
                }}
                className="w-full p-4 rounded-xl border-2 border-white/10 hover:border-blue-500/50 hover:bg-blue-500/10 transition-all text-left"
              >
                <div className="flex items-center gap-3">
                  <Volume2 className="w-5 h-5 text-blue-400" />
                  <div>
                    <p className="font-medium">Use AI voice instead</p>
                    <p className="text-xs text-viraly-muted">Generate voice from script</p>
                  </div>
                </div>
              </button>
              
              <button
                onClick={() => setShowVoiceoverTrimModal(false)}
                className="w-full p-3 text-viraly-muted hover:text-white transition-colors text-center"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </div>
      )}
      
      {/* Template Selector Modal */}
      <TemplateSelector
        isOpen={showTemplateSelector}
        onClose={() => {
          setShowTemplateSelector(false);
          window._bulkDaySelectingTemplate = null;
        }}
        onSelect={(template) => {
          // Check if we're selecting for a specific bulk day
          const bulkDay = window._bulkDaySelectingTemplate;
          if (bulkDay && (bulkContentMode === "same_content_different_template" || bulkContentMode === "different_content_different_template")) {
            // Update bulk day details with the selected template
            setBulkDayDetails(prev => ({
              ...prev,
              [bulkDay]: {
                ...prev[bulkDay],
                template: template
              }
            }));
            window._bulkDaySelectingTemplate = null;
          } else {
            // Normal template selection (Day 1 / single post)
            setSelectedTemplate(template);
          }
          setShowTemplateSelector(false);
        }}
        type={outputType === "video" ? "video" : "image"}
        selectedTemplate={selectedTemplate}
        suggestedCategory={smartDetection?.detected_category}
        suggestedTemplates={smartDetection?.suggested_templates}
      />
      
      {/* Resume from Saved Clip Modal (P2) */}
      {showResumeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-lg mx-4 bg-viraly-dark border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
          >
            <div className="p-6 border-b border-white/10">
              <h3 className="font-heading text-xl font-bold flex items-center gap-2">
                <Video className="w-5 h-5 text-purple-400" />
                Resume Saved Videos
              </h3>
              <p className="text-sm text-viraly-muted mt-1">
                These videos had partial generation and can be recovered
              </p>
            </div>
            
            <div className="p-6 max-h-[60vh] overflow-y-auto space-y-3">
              {partialClips.map((clip, index) => (
                <div
                  key={clip.video_id || index}
                  className="p-4 rounded-xl bg-white/5 border border-white/10 hover:border-purple-500/30 transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-viraly-light">
                        {clip.title || `Video ${index + 1}`}
                      </p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-viraly-muted">
                          {clip.progress || 0}% complete
                        </span>
                        {clip.clips_count > 0 && (
                          <span className="text-xs text-green-400">
                            {clip.clips_count} clips saved
                          </span>
                        )}
                        {clip.created_at && (
                          <span className="text-xs text-viraly-muted">
                            {new Date(clip.created_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleResumeVideo(clip.video_id)}
                      disabled={resumingVideoId === clip.video_id}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {resumingVideoId === clip.video_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>Resume</>
                      )}
                    </Button>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="mt-3 h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-purple-500 rounded-full transition-all"
                      style={{ width: `${clip.progress || 0}%` }}
                    />
                  </div>
                </div>
              ))}
              
              {partialClips.length === 0 && (
                <p className="text-center text-viraly-muted py-8">
                  No saved videos to resume
                </p>
              )}
            </div>
            
            <div className="p-4 border-t border-white/10 bg-white/5">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setShowResumeModal(false)}
              >
                Close
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </DashboardLayout>
  );
}
